import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemText,
  Menu,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import NotesIcon from '@mui/icons-material/Notes';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RuleIcon from '@mui/icons-material/Rule';
import SaveIcon from '@mui/icons-material/Save';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import {
  addKeywordClusterMappings,
  createKeywordClusterEdge,
  createKeywordClusterTopic,
  deleteKeywordClusterEdge,
  deleteKeywordClusterTopic,
  getAggregate,
  getKeywordClusterTree,
  getFilterPreference,
  getFilterOptions,
  getKeywordOverview,
  getNotes,
  getUserLogbook,
  getUsersProgress,
  markDuplicateFollowUp,
  markDuplicateReviewed,
  removeDuplicateFollowUp,
  removeKeywordClusterMappings,
  saveFilterPreference,
  saveNote,
  undoLastKeywordClusterAction,
  updateKeywordClusterTopic,
} from '../api/statistics';
import {
  buildLogbuchPdfDocument,
  buildLogbuchPdfHeader,
  buildLogbuchPdfStyles,
  loadLogbuchPdfBrandingAssets,
} from './pdf/logbuchPdfBuilder';
import KeywordClusterEditor from './KeywordClusterEditor';
import { buildLogbuchCombinedFilename, buildLogbuchSingleFilename } from './pdf/logbuchPdfFilename';
import { useAuth } from '../contexts/AuthContext';
import {
  getComplianceAttentionSummary,
  getComplianceLightPalette,
} from '../config/complianceLightStatus';
import { canManageStatisticsForeignEntries } from '../config/roleAccess';
import {
  WACHBEREITSCHAFT_LABELS,
  WACHBEREITSCHAFT_VALUES,
  getWachbereitschaftShortLabel,
  normalizeWachbereitschaftValue,
} from '../config/wachbereitschaft';
import LogbookEntryEditDialog from './LogbookEntryEditDialog';
import LogbookEntryDeleteDialog from './LogbookEntryDeleteDialog';
import StatisticsLogbookDeleteRequestDialog from './StatisticsLogbookDeleteRequestDialog';
import StatisticsPendingEntryCancelDialog from './StatisticsPendingEntryCancelDialog';
import SelfReflectionQuickViewDialog from '../features/selfReflection/SelfReflectionQuickViewDialog';

pdfMake.addVirtualFileSystem(pdfFonts);

const ITEM_HEIGHT = 44;
const ITEM_PADDING_TOP = 8;
const DEFAULT_SORT_KEY = 'needs-attention';
const DEFAULT_GROUP_BY = 'none';
const DEFAULT_TIME_MODE = 'scaled';
const ROLE_LABELS = {
  admin: 'Admin',
  leiter: 'Leiter der Höhenrettung',
  lehrhöhenretter_ausbildung: 'Lehrhöhenretter, Ber. Aus. & Fortb.',
  lehrhöhenretter_technik: 'Lehrhöhenretter, Ber. Tech.',
  lehrhöhenretter_einsatztaktik: 'Lehrhöhenretter, Ber. Eins.',
  lehrhöhenretter: 'Lehrhöhenretter',
  einheitsführer: 'Einheitsführer',
  geräteprüfer: 'Geräteprüfer',
  höhenretter: 'Höhenretter',
  bewerber_srht: 'Bewerber SRHT',
  bewerber_srht_extern: 'Bewerber SRHT (Extern)',
  beobachter: 'Beobachter',
};
const SAVE_DEBOUNCE_MS = 900;
const DAY_SERVICE_SCALE_FACTOR = 1.17;
const DAY_SERVICE_ENTRY_TYPES = new Set([
  'Tagesfortbildung',
  'Tag einer Wochenfortbildung',
  'Pflichtfortbildung',
  'Tag eines Wettkampfes',
  'Trainingstag für einen Wettkampf',
  'Dozententätigkeit (Tagesdienst)',
  'Geräteprüfung (Tagesdienst)',
  'Ausbilderfortbildung (Intern)',
  'Schulung durch externen Ausbilder',
  'Externe Fortbildung',
  'Fortbildung Geräteprüfer',
  'Honorartätigkeit',
]);

const menuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 8.5 + ITEM_PADDING_TOP,
      width: 280,
    },
  },
};

function createDefaultStatisticsFilters(year) {
  return {
    year,
    sortKey: DEFAULT_SORT_KEY,
    groupBy: DEFAULT_GROUP_BY,
    timeMode: DEFAULT_TIME_MODE,
    selectedUserIds: [],
    selectedRoles: [],
    selectedWachbereitschaften: [],
    selectedResponsibleKeys: [],
    selectedEntryTypes: [],
    excludedUserIds: [],
    excludedEntryTypes: [],
  };
}

function sanitizeResponsibleKeys(values = []) {
  if (!Array.isArray(values)) {
    return [];
  }

  const seen = new Set();
  const result = [];
  values.forEach((value) => {
    let normalized = String(value || '').trim();
    if (Number.isInteger(Number(value)) && Number(value) > 0) {
      normalized = `user:${Number(value)}`;
    }

    if (!normalized || seen.has(normalized)) {
      return;
    }

    seen.add(normalized);
    result.push(normalized);
  });

  return result;
}

function sanitizeWachbereitschaftFilterValues(values = []) {
  if (!Array.isArray(values)) {
    return [];
  }

  const seen = new Set();
  const result = [];
  values.forEach((value) => {
    const raw = String(value || '').trim().toLowerCase();
    // 'none' = Benutzer ohne zugewiesene Wachbereitschaft.
    const normalized = raw === 'none' ? 'none' : normalizeWachbereitschaftValue(raw);
    if (!normalized || seen.has(normalized)) {
      return;
    }
    seen.add(normalized);
    result.push(normalized);
  });

  return result;
}

function sanitizeStatisticsFilters(rawFilters, fallbackYear) {
  const defaults = createDefaultStatisticsFilters(fallbackYear);
  const nextYear = Number.parseInt(rawFilters?.year, 10);

  return {
    year: Number.isInteger(nextYear) && nextYear > 0 ? nextYear : defaults.year,
    sortKey: String(rawFilters?.sortKey || defaults.sortKey),
    groupBy: String(rawFilters?.groupBy || defaults.groupBy),
    timeMode: String(rawFilters?.timeMode || defaults.timeMode).toLowerCase() === 'unscaled' ? 'unscaled' : defaults.timeMode,
    selectedUserIds: Array.isArray(rawFilters?.selectedUserIds) ? rawFilters.selectedUserIds.map(Number).filter(Number.isFinite) : defaults.selectedUserIds,
    selectedRoles: Array.isArray(rawFilters?.selectedRoles) ? rawFilters.selectedRoles.map((value) => String(value || '').trim()).filter(Boolean) : defaults.selectedRoles,
    selectedWachbereitschaften: sanitizeWachbereitschaftFilterValues(rawFilters?.selectedWachbereitschaften || defaults.selectedWachbereitschaften),
    selectedResponsibleKeys: sanitizeResponsibleKeys(rawFilters?.selectedResponsibleKeys || rawFilters?.selectedResponsibleIds || defaults.selectedResponsibleKeys),
    selectedEntryTypes: Array.isArray(rawFilters?.selectedEntryTypes) ? rawFilters.selectedEntryTypes.map((value) => String(value || '').trim()).filter(Boolean) : defaults.selectedEntryTypes,
    excludedUserIds: Array.isArray(rawFilters?.excludedUserIds) ? rawFilters.excludedUserIds.map(Number).filter(Number.isFinite) : defaults.excludedUserIds,
    excludedEntryTypes: Array.isArray(rawFilters?.excludedEntryTypes) ? rawFilters.excludedEntryTypes.map((value) => String(value || '').trim()).filter(Boolean) : defaults.excludedEntryTypes,
  };
}

function buildStatisticsQueryParams(filters) {
  return {
    year: filters.year,
    timeMode: filters.timeMode,
    ...(filters.selectedUserIds.length ? { userIds: filters.selectedUserIds.join(',') } : {}),
    ...(filters.selectedRoles.length ? { roles: filters.selectedRoles.join(',') } : {}),
    ...(filters.selectedWachbereitschaften.length ? { wachbereitschaften: filters.selectedWachbereitschaften.join(',') } : {}),
    ...(filters.selectedResponsibleKeys.length ? { responsibleKeys: filters.selectedResponsibleKeys.join(',') } : {}),
    ...(filters.selectedEntryTypes.length ? { entryTypes: filters.selectedEntryTypes.join(',') } : {}),
    ...(filters.excludedUserIds.length ? { excludedUserIds: filters.excludedUserIds.join(',') } : {}),
    ...(filters.excludedEntryTypes.length ? { excludedEntryTypes: filters.excludedEntryTypes.join(',') } : {}),
    sortKey: filters.sortKey,
    groupBy: filters.groupBy,
  };
}

function buildStatisticsFilterSignature(filters) {
  return JSON.stringify({
    year: filters.year,
    sortKey: filters.sortKey,
    groupBy: filters.groupBy,
    timeMode: filters.timeMode,
    selectedUserIds: [...filters.selectedUserIds].sort((left, right) => left - right),
    selectedRoles: [...filters.selectedRoles].sort((left, right) => left.localeCompare(right, 'de')),
    selectedWachbereitschaften: [...filters.selectedWachbereitschaften].sort((left, right) => left.localeCompare(right, 'de')),
    selectedResponsibleKeys: [...filters.selectedResponsibleKeys].sort((left, right) => left.localeCompare(right, 'de')),
    selectedEntryTypes: [...filters.selectedEntryTypes].sort((left, right) => left.localeCompare(right, 'de')),
    excludedUserIds: [...filters.excludedUserIds].sort((left, right) => left - right),
    excludedEntryTypes: [...filters.excludedEntryTypes].sort((left, right) => left.localeCompare(right, 'de')),
  });
}

const WACHBEREITSCHAFT_FILTER_OPTIONS = [
  ...WACHBEREITSCHAFT_VALUES.map((value) => ({ value, label: WACHBEREITSCHAFT_LABELS[value] })),
  { value: 'none', label: 'Ohne Zuordnung' },
];

function formatHours(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '-';
  }

  const totalMinutes = Math.round(numeric * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} h`;
}

function formatHoursCompact(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '-';
  }

  return `${Math.max(0, Math.round(numeric))}h`;
}

function formatHoursTight(value) {
  const formatted = formatHours(value);
  return formatted === '-' ? formatted : formatted.replace(' h', 'h');
}

function getStatisticsTimeModeLabel(timeMode) {
  return timeMode === 'scaled' ? 'skaliert' : 'Ist-Zeit';
}

function scaleValueForEntryType(entryType, value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 0;
  }

  if (!DAY_SERVICE_ENTRY_TYPES.has(String(entryType || '').trim())) {
    return numeric;
  }

  return Number((numeric * DAY_SERVICE_SCALE_FACTOR).toFixed(2));
}

function formatDate(value) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleDateString('de-DE');
}

function formatDateTime(value) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleString('de-DE');
}

function formatKeywordClusterHistoryItem(row) {
  const actionLabel = String(row?.actionLabel || '').trim() || 'Aktion ausgeführt';

  const edgeLabel = row?.sourceNodeName && row?.targetNodeName
    ? `${row.sourceNodeName} -> ${row.targetNodeName}`
    : '';
  const subject = row?.nodeName || row?.normalizedKeyword || edgeLabel;
  if (!subject) {
    return actionLabel;
  }

  return `${actionLabel}: ${subject}`;
}

function formatWachbereitschaft(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) {
    return '';
  }

  return getWachbereitschaftShortLabel(trimmed, trimmed);
}

function getComplianceShortLabel(light) {
  switch (light?.key) {
    case 'pflichtfortbildung':
      return 'PF';
    case 'objektwoche':
      return 'OW';
    case 'geraeteprueferfortbildung':
      return 'GP';
    case 'ausbilderfortbildung':
      return 'AF';
    default:
      return '?';
  }
}

function sortComplianceLights(lights = []) {
  const order = {
    pflichtfortbildung: 0,
    objektwoche: 1,
    geraeteprueferfortbildung: 2,
    ausbilderfortbildung: 3,
  };

  return [...(lights || [])].sort((left, right) => {
    const leftOrder = order[left?.key] ?? 999;
    const rightOrder = order[right?.key] ?? 999;
    return leftOrder - rightOrder;
  });
}

function buildPeriodText(filters) {
  if (filters?.from && filters?.to) {
    return `Zeitraum: ${filters.from} bis ${filters.to}`;
  }

  return null;
}

function getStatisticsSortLabel(sortKey) {
  switch (sortKey) {
    case 'name-asc':
      return 'Name A-Z';
    case 'name-desc':
      return 'Name Z-A';
    case 'total-progress-desc':
      return 'Gesamtfortschritt';
    case 'trainer-progress-desc':
      return 'Lehrhöhenretterstunden';
    case 'duplicates-open-desc':
      return 'Offene Duplikate';
    case 'last-entry-desc':
      return 'Letzter Eintrag';
    case 'needs-attention':
    default:
      return 'Handlungsbedarf';
  }
}

function formatCompactSelection(labels, emptyLabel, countLabel) {
  const normalizedLabels = (labels || []).map((label) => String(label || '').trim()).filter(Boolean);
  if (!normalizedLabels.length) {
    return emptyLabel;
  }

  const joined = normalizedLabels.slice(0, 2).join(', ');
  if (normalizedLabels.length <= 2 && joined.length <= 36) {
    return joined;
  }

  return `${normalizedLabels.length} ${countLabel}`;
}

function escapeXml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function truncateLabel(value, maxLength = 24) {
  const text = String(value || '').trim();
  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, Math.max(1, maxLength - 1)).trim()}…`;
}

function splitLabelLines(value, maxCharsPerLine = 18, maxLines = 2) {
  const text = String(value || '').trim();
  if (!text) {
    return [''];
  }

  const words = text.split(/\s+/).filter(Boolean);
  const lines = [];
  let current = '';

  words.forEach((word) => {
    if (!current) {
      if (word.length <= maxCharsPerLine) {
        current = word;
      } else {
        lines.push(truncateLabel(word, maxCharsPerLine));
        current = '';
      }
      return;
    }

    const next = `${current} ${word}`;
    if (next.length <= maxCharsPerLine) {
      current = next;
      return;
    }

    lines.push(current);
    current = word.length <= maxCharsPerLine ? word : truncateLabel(word, maxCharsPerLine);
  });

  if (current) {
    lines.push(current);
  }

  if (lines.length <= maxLines) {
    return lines;
  }

  const capped = lines.slice(0, maxLines);
  capped[maxLines - 1] = truncateLabel(capped[maxLines - 1], maxCharsPerLine);
  return capped;
}

function buildKeywordTreeSvg(keywordOverview = {}) {
  const level1 = Array.isArray(keywordOverview.clusterLevel1) ? keywordOverview.clusterLevel1.slice(0, 7) : [];
  const level2All = Array.isArray(keywordOverview.clusterLevel2) ? keywordOverview.clusterLevel2 : [];
  const selectedLevel1Keys = new Set(level1.map((item) => String(item.label || '').toLocaleLowerCase('de')));

  const level2ByL1 = new Map();
  level2All.forEach((item) => {
    const key = String(item.level1Label || '').toLocaleLowerCase('de');
    if (!selectedLevel1Keys.has(key)) {
      return;
    }

    if (!level2ByL1.has(key)) {
      level2ByL1.set(key, []);
    }
    level2ByL1.get(key).push(item);
  });

  const selectedLevel2 = [];
  level1.forEach((item) => {
    const key = String(item.label || '').toLocaleLowerCase('de');
    const children = (level2ByL1.get(key) || [])
      .sort((a, b) => Number(b.count || 0) - Number(a.count || 0))
      .slice(0, 4)
      .map((child) => ({ ...child, parentKey: key }));
    selectedLevel2.push(...children);
  });

  const width = 1140;
  const height = 350;
  const rootX = 165;
  const rootY = Math.round(height / 2);
  const l1X = 500;
  const l2X = 900;

  const rootTotal = (Array.isArray(keywordOverview.rawKeywords)
    ? keywordOverview.rawKeywords.reduce((sum, item) => sum + Number(item?.count || 0), 0)
    : 0);

  const topPadding = 32;
  const bottomPadding = 32;
  const level1Spacing = Math.max(38, (height - topPadding - bottomPadding) / Math.max(1, level1.length - 1));
  const l1Nodes = level1.map((item, index) => ({
    ...item,
    x: l1X,
    y: Math.round(level1.length === 1 ? rootY : (topPadding + (index * level1Spacing))),
    key: String(item.label || '').toLocaleLowerCase('de'),
    lines: splitLabelLines(item.label, 18, 2),
  }));

  const l2Nodes = [];
  l1Nodes.forEach((parent) => {
    const children = selectedLevel2.filter((child) => child.parentKey === parent.key);
    const step = 24;
    const blockHeight = Math.max(step, step * Math.max(1, children.length - 1));
    const startY = Math.round(parent.y - blockHeight / 2);

    children.forEach((child, index) => {
      l2Nodes.push({
        ...child,
        x: l2X,
        y: Math.max(20, Math.min(height - 20, startY + (index * step))),
        label: truncateLabel(child.label, 24),
      });
    });
  });

  const svgParts = [];
  svgParts.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`);
  svgParts.push('<defs><filter id="shadow" x="-20%" y="-20%" width="140%" height="140%"><feDropShadow dx="0" dy="1" stdDeviation="2" flood-opacity="0.18"/></filter><linearGradient id="bg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#f8fafc"/><stop offset="100%" stop-color="#eef2ff"/></linearGradient></defs>');
  svgParts.push('<rect x="0" y="0" width="100%" height="100%" fill="url(#bg)"/>');

  l1Nodes.forEach((node) => {
    const midX = Math.round((rootX + node.x) / 2);
    svgParts.push(`<path d="M ${rootX + 102} ${rootY} C ${midX - 80} ${rootY}, ${midX - 30} ${node.y}, ${node.x - 100} ${node.y}" fill="none" stroke="#94a3b8" stroke-width="1.5"/>`);
  });

  l2Nodes.forEach((node) => {
    const parent = l1Nodes.find((candidate) => candidate.key === node.parentKey);
    if (!parent) {
      return;
    }

    const midX = Math.round((parent.x + node.x) / 2);
    svgParts.push(`<path d="M ${parent.x + 100} ${parent.y} C ${midX - 65} ${parent.y}, ${midX - 20} ${node.y}, ${node.x - 94} ${node.y}" fill="none" stroke="#cbd5e1" stroke-width="1.25"/>`);
  });

  svgParts.push(`<g filter="url(#shadow)"><rect x="${rootX - 105}" y="${rootY - 32}" rx="16" ry="16" width="210" height="64" fill="#0f172a"/></g>`);
  svgParts.push(`<text x="${rootX}" y="${rootY - 8}" text-anchor="middle" fill="#f8fafc" font-family="Helvetica" font-size="14" font-weight="700">Schlagwörter</text>`);
  svgParts.push(`<text x="${rootX}" y="${rootY + 15}" text-anchor="middle" fill="#cbd5e1" font-family="Helvetica" font-size="11">Nennungen: ${rootTotal}</text>`);

  l1Nodes.forEach((node) => {
    const lineOffset = node.lines.length === 1 ? 0 : -6;
    svgParts.push(`<g filter="url(#shadow)"><rect x="${node.x - 100}" y="${node.y - 22}" rx="12" ry="12" width="200" height="44" fill="#1d4ed8"/></g>`);
    svgParts.push(`<text x="${node.x}" y="${node.y + lineOffset}" text-anchor="middle" fill="#ffffff" font-family="Helvetica" font-size="10.5" font-weight="700">`);
    node.lines.forEach((line, index) => {
      svgParts.push(`<tspan x="${node.x}" dy="${index === 0 ? 0 : 12}">${escapeXml(line)}</tspan>`);
    });
    svgParts.push('</text>');
    svgParts.push(`<text x="${node.x}" y="${node.y + 16}" text-anchor="middle" fill="#dbeafe" font-family="Helvetica" font-size="10">${Number(node.count || 0)}</text>`);
  });

  l2Nodes.forEach((node) => {
    svgParts.push(`<g filter="url(#shadow)"><rect x="${node.x - 92}" y="${node.y - 14}" rx="9" ry="9" width="184" height="28" fill="#f97316"/></g>`);
    svgParts.push(`<text x="${node.x}" y="${node.y + 4}" text-anchor="middle" fill="#ffffff" font-family="Helvetica" font-size="10" font-weight="700">${escapeXml(`${node.label} (${Number(node.count || 0)})`)}</text>`);
  });

  svgParts.push('<text x="980" y="18" text-anchor="end" fill="#475569" font-family="Helvetica" font-size="10">Ebene 1: Cluster</text>');
  svgParts.push('<text x="980" y="32" text-anchor="end" fill="#475569" font-family="Helvetica" font-size="10">Ebene 2: Untercluster</text>');

  svgParts.push('</svg>');
  return svgParts.join('');
}

function buildKeywordTreeSvgFromSavedTree(keywordClusterTree = {}) {
  const nodes = Array.isArray(keywordClusterTree?.nodes) ? keywordClusterTree.nodes : [];
  const edges = Array.isArray(keywordClusterTree?.edges) ? keywordClusterTree.edges : [];
  const mappings = Array.isArray(keywordClusterTree?.mappings) ? keywordClusterTree.mappings : [];
  if (!nodes.length) {
    return '';
  }

  const mappingsByNodeId = new Map();
  mappings.forEach((mapping) => {
    const nodeId = Number(mapping?.nodeId);
    if (!mappingsByNodeId.has(nodeId)) {
      mappingsByNodeId.set(nodeId, []);
    }
    mappingsByNodeId.get(nodeId).push(mapping);
  });

  const pad = 36;
  const titleInsetY = 20;
  const defaultColor = '#1d4ed8';

  const safeNodes = nodes.map((node) => {
    const mapped = mappingsByNodeId.get(Number(node?.id)) || [];
    return {
      ...node,
      mapped,
      width: 220,
      height: mapped.length > 0 ? 118 : 34,
      positionX: Math.max(0, Number(node?.positionX || 0)),
      positionY: Math.max(0, Number(node?.positionY || 0)),
      color: /^#[0-9a-fA-F]{6}$/.test(String(node?.color || '')) ? String(node.color) : defaultColor,
      label: String(node?.name || ''),
    };
  });

  const minX = Math.min(...safeNodes.map((node) => node.positionX));
  const minY = Math.min(...safeNodes.map((node) => node.positionY));
  const maxX = Math.max(...safeNodes.map((node) => node.positionX + node.width));
  const maxY = Math.max(...safeNodes.map((node) => node.positionY + node.height));
  const width = Math.max(720, Math.ceil((maxX - minX) + (pad * 2)));
  const height = Math.max(320, Math.ceil((maxY - minY) + (pad * 2) + titleInsetY));
  const offsetX = pad - minX;
  const offsetY = pad + titleInsetY - minY;

  const exportNodes = safeNodes.map((node) => ({
    ...node,
    x: Math.round(node.positionX + offsetX),
    y: Math.round(node.positionY + offsetY),
  }));

  function getExportNodeCenter(node) {
    return {
      x: node.x + (node.width / 2),
      y: node.y + (node.height / 2),
    };
  }

  function getExportRectEdgePoint(node, targetPoint) {
    const center = getExportNodeCenter(node);
    const halfWidth = node.width / 2;
    const halfHeight = node.height / 2;
    const deltaX = targetPoint.x - center.x;
    const deltaY = targetPoint.y - center.y;

    if (Math.abs(deltaX) < 0.001 && Math.abs(deltaY) < 0.001) {
      return center;
    }

    const scaleX = Math.abs(deltaX) > 0.001 ? halfWidth / Math.abs(deltaX) : Number.POSITIVE_INFINITY;
    const scaleY = Math.abs(deltaY) > 0.001 ? halfHeight / Math.abs(deltaY) : Number.POSITIVE_INFINITY;
    const scale = Math.min(scaleX, scaleY);

    return {
      x: center.x + (deltaX * scale),
      y: center.y + (deltaY * scale),
    };
  }

  function getExportStraightConnection(sourceNode, targetNode) {
    const sourceCenter = getExportNodeCenter(sourceNode);
    const targetCenter = getExportNodeCenter(targetNode);
    return {
      source: getExportRectEdgePoint(sourceNode, targetCenter),
      target: getExportRectEdgePoint(targetNode, sourceCenter),
    };
  }

  const nodeById = new Map(exportNodes.map((node) => [Number(node.id), node]));

  const svgParts = [];
  svgParts.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`);
  svgParts.push('<defs><filter id="shadow" x="-20%" y="-20%" width="140%" height="140%"><feDropShadow dx="0" dy="1" stdDeviation="2" flood-opacity="0.18"/></filter><linearGradient id="bgSavedTree" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#f8fafc"/><stop offset="100%" stop-color="#e2e8f0"/></linearGradient></defs>');
  svgParts.push('<rect x="0" y="0" width="100%" height="100%" fill="url(#bgSavedTree)"/>');

  edges.forEach((edge) => {
    const source = nodeById.get(Number(edge?.sourceNodeId));
    const target = nodeById.get(Number(edge?.targetNodeId));
    if (!source || !target) {
      return;
    }

    const connection = getExportStraightConnection(source, target);
    svgParts.push(`<line x1="${connection.source.x}" y1="${connection.source.y}" x2="${connection.target.x}" y2="${connection.target.y}" fill="none" stroke="#64748b" stroke-width="1.8" stroke-linecap="round"/>`);
    svgParts.push(`<circle cx="${connection.source.x}" cy="${connection.source.y}" r="2.2" fill="#64748b"/>`);
    svgParts.push(`<circle cx="${connection.target.x}" cy="${connection.target.y}" r="2.2" fill="#64748b"/>`);
  });

  exportNodes.forEach((node) => {
    const x = node.x;
    const y = node.y;
    const mapped = node.mapped || [];
    const label = truncateLabel(node.label || '-', 26);
    const keywordLabel = mapped
      .slice(0, 3)
      .map((item) => truncateLabel(item.rawKeywordLabel || item.normalizedKeyword || '', 24))
      .filter(Boolean)
      .join(' | ');

    if (mapped.length > 0) {
      svgParts.push(`<g filter="url(#shadow)"><rect x="${x}" y="${y}" rx="12" ry="12" width="${node.width}" height="${node.height}" fill="#ffffff" stroke="#cbd5e1" stroke-width="1"/></g>`);
      svgParts.push(`<rect x="${x}" y="${y}" rx="12" ry="12" width="${node.width}" height="30" fill="${escapeXml(node.color)}"/>`);
      svgParts.push(`<text x="${x + 12}" y="${y + 20}" fill="#ffffff" font-family="Helvetica" font-size="12" font-weight="700">${escapeXml(`${label} (${mapped.length})`)}</text>`);
      svgParts.push(`<text x="${x + 12}" y="${y + 48}" fill="#334155" font-family="Helvetica" font-size="10">Zuordnungen: ${mapped.length}</text>`);
      svgParts.push(`<text x="${x + 12}" y="${y + 66}" fill="#475569" font-family="Helvetica" font-size="9">${escapeXml(keywordLabel || 'Keine Rohbegriffe zugeordnet')}</text>`);
      if (mapped.length > 3) {
        svgParts.push(`<text x="${x + 12}" y="${y + 82}" fill="#64748b" font-family="Helvetica" font-size="9">+${mapped.length - 3} weitere Begriffe</text>`);
      }
      return;
    }

    svgParts.push(`<g filter="url(#shadow)"><rect x="${x}" y="${y}" rx="12" ry="12" width="${node.width}" height="${node.height}" fill="${escapeXml(node.color)}"/></g>`);
    svgParts.push(`<text x="${x + 12}" y="${y + 21}" fill="#ffffff" font-family="Helvetica" font-size="12" font-weight="700">${escapeXml(`${label} (0)`)}</text>`);
  });

  svgParts.push(`<text x="${width - 18}" y="18" text-anchor="end" fill="#475569" font-family="Helvetica" font-size="10">Kategorienbaum (gespeicherte Jahresansicht)</text>`);
  svgParts.push('</svg>');
  return svgParts.join('');
}

function buildCombinedPdf(perUser, totals, year, filters, progressMap, brandingAssets = {}, keywordOverview = {}, keywordClusterTree = null) {
  const periodText = buildPeriodText(filters);
  const allEntries = (perUser || []).flatMap((userBlock) => userBlock.entries || []);
  const scaledTotals = allEntries.reduce((sum, entry) => {
    const pause = Number(entry?.applied_pause_hours ?? entry?.pause_hours ?? 0);
    const service = Number(entry?.service_time || 0);
    const prep = Number(entry?.prep_time || 0);
    return {
      pause: sum.pause + scaleValueForEntryType(entry?.entry_type, pause),
      service: sum.service + scaleValueForEntryType(entry?.entry_type, service),
      prep: sum.prep + scaleValueForEntryType(entry?.entry_type, prep),
    };
  }, { pause: 0, service: 0, prep: 0 });

  const content = [
    buildLogbuchPdfHeader({ assets: brandingAssets, title: `Sammel-Logbuch ${year}` }),
    ...(periodText ? [{ text: periodText, style: 'meta', margin: [0, 0, 0, 6] }] : []),
    {
      text: `Benutzer: ${perUser.length} | HöRett: ${formatHours(totals?.hours || 0)} | Lehrhöhenretter: ${formatHours(totals?.lehrhHours || 0)} | Pause: ${formatHours(totals?.pauseTime || 0)} | Vor-/Nachbereitung: ${formatHours(totals?.prepTime || 0)} | Dienstzeit: ${formatHours(totals?.serviceTime || 0)}`,
      style: 'meta',
      margin: [0, 0, 0, 8],
    },
    {
      text: `Summe aller Dienstzeiten: ${formatHoursTight(totals?.serviceTime || 0)} (Skaliert: ${formatHoursTight(scaledTotals.service)})`,
      style: 'sum',
      margin: [0, 0, 0, 0],
    },
    {
      text: `Summe aller Vor-/Nachbereitungen: ${formatHoursTight(totals?.prepTime || 0)} (Skaliert: ${formatHoursTight(scaledTotals.prep)})`,
      style: 'sum',
      margin: [0, 0, 0, 0],
    },
    {
      text: `Summe aller Pausenzeiten: ${formatHoursTight(totals?.pauseTime || 0)} (Skaliert: ${formatHoursTight(scaledTotals.pause)})`,
      style: 'sum',
      margin: [0, 0, 0, 12],
    },
    {
      text: 'Kategorienbaum (Schlagwörter)',
      style: 'subheader',
      margin: [0, 4, 0, 6],
    },
    ...((Array.isArray(keywordClusterTree?.nodes) && keywordClusterTree.nodes.length > 0)
      ? [{
        svg: buildKeywordTreeSvgFromSavedTree(keywordClusterTree),
        fit: [790, 340],
        margin: [0, 0, 0, 0],
      }]
      : (Array.isArray(keywordOverview?.rawKeywords) && keywordOverview.rawKeywords.length > 0
      ? [{
        svg: buildKeywordTreeSvg(keywordOverview),
        fit: [790, 240],
        margin: [0, 0, 0, 0],
      }]
      : [{
        text: 'Keine Schlagwörter im gewählten Zeitraum/Filter vorhanden.',
        style: 'meta',
        margin: [0, 0, 0, 0],
      }])),
  ];

  let styles = buildLogbuchPdfStyles();

  perUser.forEach((userBlock, index) => {
    const doc = buildLogbuchPdfDocument({
      fullName: userBlock.full_name,
      year,
      entries: userBlock.entries,
      complianceLights: progressMap.get(Number(userBlock.userId))?.complianceLights || [],
      periodText,
      brandingAssets,
      headerTitle: 'Logbuch',
    });

    if (doc.styles) {
      styles = { ...styles, ...doc.styles };
    }

    const sectionContent = (doc.content || []).map((item, itemIndex) => {
      if (index === 0 && itemIndex === 0) {
        return { ...item, pageBreak: 'before' };
      }

      if (index > 0 && itemIndex === 0) {
        return { ...item, pageBreak: 'before' };
      }

      return item;
    });

    content.push(...sectionContent);
  });

  return {
    pageOrientation: 'landscape',
    content,
    styles,
  };
}

function StatisticsView() {
  const currentYear = new Date().getFullYear();
  const { user } = useAuth();

  const [filters, setFilters] = useState(() => createDefaultStatisticsFilters(currentYear));

  const [filterOptions, setFilterOptions] = useState({ users: [], responsibles: [], entryTypes: [], roles: [] });
  const [progressRows, setProgressRows] = useState([]);
  const [aggregate, setAggregate] = useState({
    totals: { hours: 0, pauseTime: 0, prepTime: 0, serviceTime: 0, entries: 0 },
    displayTotals: { pauseTime: 0, prepTime: 0, serviceTime: 0 },
    perUser: [],
    filters: {},
  });
  const [filterPanelExpanded, setFilterPanelExpanded] = useState(false);
  const [preferencesReady, setPreferencesReady] = useState(false);
  const [preferenceMeta, setPreferenceMeta] = useState({ updatedAt: null, updatedByName: null });
  const [preferenceSaveState, setPreferenceSaveState] = useState('idle');
  const [draftNotes, setDraftNotes] = useState({});
  const [savedNotesByUserId, setSavedNotesByUserId] = useState({});
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [detailByUserId, setDetailByUserId] = useState({});
  const [detailLoadingByUserId, setDetailLoadingByUserId] = useState({});
  const [detailErrorByUserId, setDetailErrorByUserId] = useState({});
  const [noteSavingByUserId, setNoteSavingByUserId] = useState({});
  const [duplicatePanelExpandedByUserId, setDuplicatePanelExpandedByUserId] = useState({});
  const [duplicateFollowUpBusyKey, setDuplicateFollowUpBusyKey] = useState('');
  const [entryActionMenu, setEntryActionMenu] = useState({ anchorEl: null, entry: null, userId: null });
  const [entryRequestDialog, setEntryRequestDialog] = useState(null);
  const [selfReflectionQuickView, setSelfReflectionQuickView] = useState(null);
  const [expandedKeywordEntryIds, setExpandedKeywordEntryIds] = useState({});
  const [keywordOverview, setKeywordOverview] = useState({
    canManage: false,
    topics: [],
    rawKeywords: [],
    canonicalKeywords: [],
    clusterLevel1: [],
    clusterLevel2: [],
  });
  const [keywordClusterTree, setKeywordClusterTree] = useState({
    year: null,
    canManage: false,
    tree: null,
    nodes: [],
    edges: [],
    mappings: [],
    rawKeywords: [],
    history: [],
  });
  const [keywordPanelExpanded, setKeywordPanelExpanded] = useState(false);
  const [keywordEditorDragging, setKeywordEditorDragging] = useState(false);
  const [keywordLoading, setKeywordLoading] = useState(false);

  const [error, setError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');

  const lastSavedFilterSignatureRef = useRef('');

  const canPersistGlobalFilters = useMemo(
    () => ['admin', 'leiter', 'lehrhöhenretter_ausbildung'].some((role) => (user?.roles || []).includes(role)),
    [user?.roles],
  );

  const queryParams = useMemo(() => buildStatisticsQueryParams(filters), [filters]);
  const filterSignature = useMemo(() => buildStatisticsFilterSignature(filters), [filters]);
  const dataResetSignature = useMemo(() => JSON.stringify({
    year: filters.year,
    timeMode: filters.timeMode,
    selectedUserIds: filters.selectedUserIds,
    selectedRoles: filters.selectedRoles,
    selectedResponsibleKeys: filters.selectedResponsibleKeys,
    selectedEntryTypes: filters.selectedEntryTypes,
    excludedUserIds: filters.excludedUserIds,
    excludedEntryTypes: filters.excludedEntryTypes,
  }), [filters]);

  const updateFilters = useCallback((nextValue) => {
    setFilters((previous) => {
      const nextFilters = typeof nextValue === 'function' ? nextValue(previous) : { ...previous, ...nextValue };
      return sanitizeStatisticsFilters(nextFilters, currentYear);
    });
  }, [currentYear]);

  const usersById = useMemo(() => {
    const map = new Map();
    filterOptions.users.forEach((user) => map.set(Number(user.id), user));
    return map;
  }, [filterOptions.users]);

  const responsiblesById = useMemo(() => {
    const map = new Map();
    filterOptions.responsibles.forEach((responsible) => map.set(String(responsible.id), responsible));
    return map;
  }, [filterOptions.responsibles]);

  const preferenceStatusLabel = useMemo(() => {
    if (!canPersistGlobalFilters) {
      return preferenceMeta.updatedAt
        ? `Globale Standardansicht aktiv${preferenceMeta.updatedByName ? ` · zuletzt durch ${preferenceMeta.updatedByName}` : ''}`
        : 'Globale Standardansicht aktiv';
    }

    if (preferenceSaveState === 'saving') {
      return 'Speichert globale Standardansicht...';
    }

    if (preferenceSaveState === 'saved') {
      return 'Globale Standardansicht gespeichert';
    }

    if (preferenceSaveState === 'error') {
      return 'Speichern der Standardansicht fehlgeschlagen';
    }

    return preferenceMeta.updatedAt
      ? `Auto-Save aktiv${preferenceMeta.updatedByName ? ` · zuletzt durch ${preferenceMeta.updatedByName}` : ''}`
      : 'Auto-Save für globale Standardansicht aktiv';
  }, [canPersistGlobalFilters, preferenceMeta.updatedAt, preferenceMeta.updatedByName, preferenceSaveState]);

  const compactPreferenceStatusLabel = useMemo(() => {
    if (!canPersistGlobalFilters) {
      return 'Standardansicht aktiv';
    }

    if (preferenceSaveState === 'saving') {
      return 'Speichert...';
    }

    if (preferenceSaveState === 'saved') {
      return 'Gespeichert';
    }

    if (preferenceSaveState === 'error') {
      return 'Speicherfehler';
    }

    return 'Auto-Save aktiv';
  }, [canPersistGlobalFilters, preferenceSaveState]);

  const loadInitialData = useCallback(async () => {
    try {
      setError('');
      const [optionsRes, preferenceRes] = await Promise.all([
        getFilterOptions(),
        getFilterPreference({ year: currentYear }),
      ]);

      setFilterOptions(optionsRes.data || { users: [], responsibles: [], entryTypes: [], roles: [] });

      const nextFilters = sanitizeStatisticsFilters(preferenceRes.data?.filters || {}, currentYear);
      setFilters(nextFilters);
      lastSavedFilterSignatureRef.current = buildStatisticsFilterSignature(nextFilters);
      setPreferenceMeta(preferenceRes.data?.meta || { updatedAt: null, updatedByName: null });
      setPreferenceSaveState('idle');
      setPreferencesReady(true);
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Statistikdaten konnten nicht geladen werden.');
      setPreferencesReady(true);
    }
  }, [currentYear]);

  const loadBaseData = useCallback(async () => {
    if (!preferencesReady) {
      return;
    }

    try {
      setError('');
      const [progressRes, notesRes] = await Promise.all([
        getUsersProgress(queryParams),
        getNotes(filters.year),
      ]);

      setProgressRows(progressRes.data?.users || []);

      const nextNotes = {};
      const nextSavedNotes = {};
      (notesRes.data?.notes || []).forEach((item) => {
        nextNotes[item.user_id] = item.note || '';
        nextSavedNotes[item.user_id] = Boolean(String(item.note || '').trim());
      });
      setDraftNotes(nextNotes);
      setSavedNotesByUserId(nextSavedNotes);
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Statistikdaten konnten nicht geladen werden.');
    }
  }, [filters.year, preferencesReady, queryParams]);

  const loadAggregate = useCallback(async () => {
    if (!preferencesReady) {
      return;
    }

    try {
      setError('');
      const response = await getAggregate(queryParams);
      setAggregate(response.data || {
        totals: { hours: 0, pauseTime: 0, prepTime: 0, serviceTime: 0, entries: 0 },
        displayTotals: { pauseTime: 0, prepTime: 0, serviceTime: 0 },
        perUser: [],
        filters: {},
      });
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Auswertung konnte nicht geladen werden.');
    }
  }, [preferencesReady, queryParams]);

  const loadKeywordData = useCallback(async ({ showLoading = true } = {}) => {
    if (!preferencesReady) {
      return;
    }

    try {
      if (showLoading) {
        setKeywordLoading(true);
      }
      const [overviewResponse, treeResponse] = await Promise.all([
        getKeywordOverview(queryParams),
        getKeywordClusterTree({ year: filters.year }),
      ]);
      const overviewData = overviewResponse.data || {};
      const treeData = treeResponse.data || {};

      setKeywordOverview({
        canManage: Boolean(overviewData.canManage),
        topics: Array.isArray(overviewData.topics) ? overviewData.topics : [],
        rawKeywords: Array.isArray(overviewData.rawKeywords) ? overviewData.rawKeywords : [],
        canonicalKeywords: Array.isArray(overviewData.canonicalKeywords) ? overviewData.canonicalKeywords : [],
        clusterLevel1: Array.isArray(overviewData.clusterLevel1) ? overviewData.clusterLevel1 : [],
        clusterLevel2: Array.isArray(overviewData.clusterLevel2) ? overviewData.clusterLevel2 : [],
      });

      setKeywordClusterTree({
        year: Number(treeData.year || filters.year),
        canManage: Boolean(treeData.canManage),
        tree: treeData.tree || null,
        nodes: Array.isArray(treeData.nodes) ? treeData.nodes : [],
        edges: Array.isArray(treeData.edges) ? treeData.edges : [],
        mappings: Array.isArray(treeData.mappings) ? treeData.mappings : [],
        rawKeywords: Array.isArray(treeData.rawKeywords) ? treeData.rawKeywords : [],
        history: Array.isArray(treeData.history) ? treeData.history : [],
      });
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Schlagwortdaten konnten nicht geladen werden.');
    } finally {
      if (showLoading) {
        setKeywordLoading(false);
      }
    }
  }, [filters.year, preferencesReady, queryParams]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (!preferencesReady || !canPersistGlobalFilters) {
      return undefined;
    }

    if (filterSignature === lastSavedFilterSignatureRef.current) {
      return undefined;
    }

    setPreferenceSaveState('saving');
    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await saveFilterPreference(filters);
        lastSavedFilterSignatureRef.current = filterSignature;
        setPreferenceMeta(response.data?.meta || { updatedAt: null, updatedByName: null });
        setPreferenceSaveState('saved');
        window.setTimeout(() => {
          setPreferenceSaveState((previous) => (previous === 'saved' ? 'idle' : previous));
        }, 1800);
      } catch (saveError) {
        setPreferenceSaveState('error');
        setError(saveError.response?.data?.error || 'Globale Standardansicht konnte nicht gespeichert werden.');
      }
    }, SAVE_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [canPersistGlobalFilters, filterSignature, filters, preferencesReady]);

  useEffect(() => {
    if (!preferencesReady) {
      return;
    }

    loadBaseData();
  }, [loadBaseData, preferencesReady]);

  useEffect(() => {
    if (!preferencesReady) {
      return;
    }

    loadAggregate();
  }, [loadAggregate, preferencesReady]);

  useEffect(() => {
    if (!preferencesReady) {
      return;
    }

    loadKeywordData();
  }, [loadKeywordData, preferencesReady]);

  useEffect(() => {
    setExpandedUserId(null);
    setDetailByUserId({});
    setDetailErrorByUserId({});
    setDetailLoadingByUserId({});
    setExpandedKeywordEntryIds({});
    setKeywordClusterTree((previous) => ({
      ...previous,
      year: filters.year,
      nodes: [],
      edges: [],
      mappings: [],
      rawKeywords: [],
      history: [],
    }));
  }, [dataResetSignature, filters.year]);

  const handleUndoLastKeywordTreeAction = useCallback(async () => {
    try {
      setError('');
      setSaveMessage('');
      await undoLastKeywordClusterAction({ year: filters.year });
      setSaveMessage('Letzte Baum-Aktion wurde rückgängig gemacht.');
      await loadKeywordData();
    } catch (undoError) {
      setError(undoError.response?.data?.error || 'Letzte Baum-Aktion konnte nicht rückgängig gemacht werden.');
    }
  }, [filters.year, loadKeywordData]);

  const handleEditorCreateTopic = useCallback(async (payload) => {
    await createKeywordClusterTopic({
      year: filters.year,
      name: payload?.name,
      color: payload?.color,
      positionX: payload?.positionX,
      positionY: payload?.positionY,
    });
    setSaveMessage('Kategorie erstellt.');
    await loadKeywordData({ showLoading: false });
  }, [filters.year, loadKeywordData]);

  const handleEditorUpdateTopic = useCallback(async (topicId, payload) => {
    await updateKeywordClusterTopic(topicId, {
      year: filters.year,
      ...payload,
    });
    await loadKeywordData({ showLoading: false });
  }, [filters.year, loadKeywordData]);

  const handleEditorDeleteTopic = useCallback(async (topicId) => {
    await deleteKeywordClusterTopic(topicId, { year: filters.year });
    setSaveMessage('Kategorie gelöscht.');
    await loadKeywordData({ showLoading: false });
  }, [filters.year, loadKeywordData]);

  const handleEditorCreateEdge = useCallback(async (sourceNodeId, targetNodeId) => {
    await createKeywordClusterEdge({
      year: filters.year,
      sourceNodeId,
      targetNodeId,
    });
    setSaveMessage('Verbindung erstellt.');
    await loadKeywordData({ showLoading: false });
  }, [filters.year, loadKeywordData]);

  const handleEditorDeleteEdge = useCallback(async (edgeId) => {
    await deleteKeywordClusterEdge(edgeId, { year: filters.year });
    setSaveMessage('Verbindung entfernt.');
    await loadKeywordData({ showLoading: false });
  }, [filters.year, loadKeywordData]);

  const handleEditorAddMappings = useCallback(async (topicId, normalizedKeywords = []) => {
    await addKeywordClusterMappings({
      year: filters.year,
      topicId,
      normalizedKeywords,
    });
    setSaveMessage('Zuordnungen gespeichert.');
    await loadKeywordData({ showLoading: false });
  }, [filters.year, loadKeywordData]);

  const handleEditorRemoveMappings = useCallback(async (topicId, normalizedKeywords = []) => {
    await removeKeywordClusterMappings({
      year: filters.year,
      topicId,
      normalizedKeywords,
    });
    setSaveMessage('Zuordnungen entfernt.');
    await loadKeywordData({ showLoading: false });
  }, [filters.year, loadKeywordData]);

  const progressByUser = useMemo(() => {
    const map = new Map();
    progressRows.forEach((item) => map.set(Number(item.userId), item));
    return map;
  }, [progressRows]);

  const visibleProgressRows = useMemo(() => {
    const sorted = [...progressRows].sort((left, right) => {
      if (filters.sortKey === 'needs-attention') {
        const duplicateDelta = Number(right.duplicateSummary?.open || 0) - Number(left.duplicateSummary?.open || 0);
        if (duplicateDelta !== 0) {
          return duplicateDelta;
        }

        const rightComplianceAttention = getComplianceAttentionSummary(right.complianceLights);
        const leftComplianceAttention = getComplianceAttentionSummary(left.complianceLights);
        const redDelta = rightComplianceAttention.redCount - leftComplianceAttention.redCount;
        if (redDelta !== 0) {
          return redDelta;
        }

        const yellowDelta = rightComplianceAttention.yellowCount - leftComplianceAttention.yellowCount;
        if (yellowDelta !== 0) {
          return yellowDelta;
        }

        const openDelta = rightComplianceAttention.openCount - leftComplianceAttention.openCount;
        if (openDelta !== 0) {
          return openDelta;
        }

        const totalProgressDelta = Number(left.total_progress || 0) - Number(right.total_progress || 0);
        if (totalProgressDelta !== 0) {
          return totalProgressDelta;
        }

        const trainerProgressDelta = Number(left.trainer_progress || 0) - Number(right.trainer_progress || 0);
        if (trainerProgressDelta !== 0) {
          return trainerProgressDelta;
        }

        return String(left.full_name || '').localeCompare(String(right.full_name || ''), 'de');
      }

      if (filters.sortKey === 'name-desc') {
        return String(right.full_name || '').localeCompare(String(left.full_name || ''), 'de');
      }

      if (filters.sortKey === 'total-progress-desc') {
        return Number(right.total_progress || 0) - Number(left.total_progress || 0);
      }

      if (filters.sortKey === 'trainer-progress-desc') {
        return Number(right.trainer_progress || 0) - Number(left.trainer_progress || 0);
      }

      if (filters.sortKey === 'duplicates-open-desc') {
        const duplicateDelta = Number(right.duplicateSummary?.open || 0) - Number(left.duplicateSummary?.open || 0);
        if (duplicateDelta !== 0) {
          return duplicateDelta;
        }
      }

      if (filters.sortKey === 'last-entry-desc') {
        return new Date(right.last_entry_date || 0).getTime() - new Date(left.last_entry_date || 0).getTime();
      }

      return String(left.full_name || '').localeCompare(String(right.full_name || ''), 'de');
    });

    return sorted;
  }, [filters.sortKey, progressRows]);

  const visibleProgressGroups = useMemo(() => {
    if (filters.groupBy !== 'wachbereitschaft') {
      return [{ key: 'all', label: 'Angezeigte Benutzer', rows: visibleProgressRows }];
    }

    const grouped = new Map([
      ...WACHBEREITSCHAFT_VALUES.map((value) => [value, []]),
      ['none', []],
    ]);

    visibleProgressRows.forEach((row) => {
      const normalized = normalizeWachbereitschaftValue(row.wachbereitschaft) || 'none';
      grouped.get(normalized).push(row);
    });

    // Reihenfolge entspricht WACHBEREITSCHAFT_VALUES; "Ohne Wachbereitschaft" zuletzt.
    return [
      ...WACHBEREITSCHAFT_VALUES.map((value) => ({
        key: value,
        label: WACHBEREITSCHAFT_LABELS[value],
        rows: grouped.get(value) || [],
      })),
      { key: 'none', label: 'Ohne Wachbereitschaft', rows: grouped.get('none') || [] },
    ].filter((group) => group.rows.length > 0);
  }, [filters.groupBy, visibleProgressRows]);

  const loadUserDetail = useCallback(async (userId) => {
    try {
      setDetailLoadingByUserId((prev) => ({ ...prev, [userId]: true }));
      setDetailErrorByUserId((prev) => ({ ...prev, [userId]: '' }));

      const response = await getUserLogbook(userId, queryParams);

      setDetailByUserId((prev) => ({ ...prev, [userId]: response.data }));
      setDraftNotes((prev) => ({
        ...prev,
        [userId]: typeof prev[userId] === 'string' ? prev[userId] : (response.data?.note?.note || ''),
      }));
      setSavedNotesByUserId((prev) => ({
        ...prev,
        [userId]: Boolean(String(response.data?.note?.note || '').trim()),
      }));
    } catch (loadError) {
      setDetailErrorByUserId((prev) => ({
        ...prev,
        [userId]: loadError.response?.data?.error || 'Benutzer-Logbuch konnte nicht geladen werden.',
      }));
    } finally {
      setDetailLoadingByUserId((prev) => ({ ...prev, [userId]: false }));
    }
  }, [queryParams]);

  const handleSaveNote = async (userId) => {
    try {
      setError('');
      setSaveMessage('');
      setNoteSavingByUserId((prev) => ({ ...prev, [userId]: true }));
      await saveNote(userId, filters.year, draftNotes[userId] || '');
      setSaveMessage('Bemerkung gespeichert.');
      await Promise.all([loadBaseData(), loadUserDetail(userId)]);
    } catch (saveError) {
      setError(saveError.response?.data?.error || 'Bemerkung konnte nicht gespeichert werden.');
    } finally {
      setNoteSavingByUserId((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleToggleAccordion = async (userId, expanded) => {
    setExpandedUserId(expanded ? userId : null);
    if (expanded) {
      setDuplicatePanelExpandedByUserId((prev) => ({ ...prev, [userId]: false }));
    }
    if (expanded && !detailByUserId[userId] && !detailLoadingByUserId[userId]) {
      await loadUserDetail(userId);
    }
  };

  const openEntryRequestDialog = (action, scope) => {
    const pendingCreateMode = scope === 'pending-create';
    const pendingGroupMode = Boolean(entryActionMenu.pendingCreate) && scope === 'group';
    const ownerMode = !pendingCreateMode && !pendingGroupMode && scope === 'single' && Number(entryActionMenu.userId) === Number(user?.id);
    setEntryRequestDialog({
      action,
      scope,
      ownerMode,
      entryId: pendingCreateMode
        ? Number(entryActionMenu.entry?.requestId)
        : pendingGroupMode
          ? Number(entryActionMenu.entry?.groupAnchorEntryId)
          : Number(entryActionMenu.entry?.id),
      userId: Number(entryActionMenu.userId),
    });
    setEntryActionMenu({ anchorEl: null, entry: null, userId: null });
    setError('');
    setSaveMessage('');
  };

  const handleEntryRequestSubmitted = async (action, count = 1) => {
    const detailUserId = entryRequestDialog?.userId;
    setEntryRequestDialog(null);
    setSaveMessage(`${count} Eintrag${count === 1 ? '' : 'e'} wurde${count === 1 ? '' : 'n'} zur ${action === 'delete' ? 'Löschung' : 'Änderung'} verarbeitet.`);
    await Promise.all([
      loadAggregate(),
      loadBaseData(),
      detailUserId ? loadUserDetail(detailUserId) : Promise.resolve(),
    ]);
  };

  const handleOwnerEntryChanged = async (action) => {
    const detailUserId = entryRequestDialog?.userId;
    setEntryRequestDialog(null);
    setSaveMessage(action === 'delete' ? 'Eintrag gelöscht.' : 'Eintrag gespeichert.');
    await Promise.all([
      loadAggregate(),
      loadBaseData(),
      detailUserId ? loadUserDetail(detailUserId) : Promise.resolve(),
    ]);
  };

  const handlePendingRequestChanged = async (action) => {
    const detailUserId = entryRequestDialog?.userId;
    setEntryRequestDialog(null);
    setSaveMessage(action === 'cancel'
      ? 'Eintragsanfrage wurde zurückgezogen.'
      : 'Eintragsanfrage wurde aktualisiert.');
    await Promise.all([
      loadAggregate(),
      loadBaseData(),
      detailUserId ? loadUserDetail(detailUserId) : Promise.resolve(),
    ]);
  };

  const handleReviewDuplicate = async (userId, duplicateKey) => {
    try {
      setError('');
      setSaveMessage('');
      await markDuplicateReviewed(userId, duplicateKey, '');
      setSaveMessage('Duplikatmeldung als geprüft markiert.');
      await Promise.all([loadBaseData(), loadUserDetail(userId)]);
    } catch (reviewError) {
      setError(reviewError.response?.data?.error || 'Duplikatmeldung konnte nicht gespeichert werden.');
    }
  };

  const handleMarkDuplicateFollowUp = async (userId, duplicateKey) => {
    try {
      setError('');
      setSaveMessage('');
      setDuplicateFollowUpBusyKey(duplicateKey);
      const response = await markDuplicateFollowUp(userId, duplicateKey);
      setDetailByUserId((prev) => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          duplicates: (prev[userId]?.duplicates || []).map((duplicate) => (
            duplicate.duplicateKey === duplicateKey
              ? { ...duplicate, followUp: response.data?.followUp || null }
              : duplicate
          )),
        },
      }));
      setSaveMessage('Aufforderung zur Logbuchänderung gespeichert.');
    } catch (followUpError) {
      setError(followUpError.response?.data?.error || 'Aufforderung zur Logbuchänderung konnte nicht gespeichert werden.');
    } finally {
      setDuplicateFollowUpBusyKey('');
    }
  };

  const handleRemoveDuplicateFollowUp = async (userId, duplicateKey) => {
    try {
      setError('');
      setSaveMessage('');
      setDuplicateFollowUpBusyKey(duplicateKey);
      await removeDuplicateFollowUp(userId, duplicateKey);
      setDetailByUserId((prev) => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          duplicates: (prev[userId]?.duplicates || []).map((duplicate) => (
            duplicate.duplicateKey === duplicateKey
              ? { ...duplicate, followUp: null }
              : duplicate
          )),
        },
      }));
      setSaveMessage('Aufforderung zur Logbuchänderung entfernt.');
    } catch (followUpError) {
      setError(followUpError.response?.data?.error || 'Aufforderung zur Logbuchänderung konnte nicht entfernt werden.');
    } finally {
      setDuplicateFollowUpBusyKey('');
    }
  };

  const handleExportSinglePdfs = async () => {
    const targets = aggregate.perUser;
    if (!targets.length) return;

    const brandingAssets = await loadLogbuchPdfBrandingAssets();

    targets.forEach((userBlock) => {
      const filters = aggregate.filters || {};
      const periodText = buildPeriodText(filters);
      const complianceLights = progressByUser.get(Number(userBlock.userId))?.complianceLights || [];
      const doc = buildLogbuchPdfDocument({
        fullName: userBlock.full_name,
        year: filters.year,
        entries: userBlock.entries,
        complianceLights,
        periodText,
        brandingAssets,
      });
      const fileName = buildLogbuchSingleFilename({ fullName: userBlock.full_name, year: filters.year });
      pdfMake.createPdf(doc).download(fileName);
    });
  };

  const handleExportCombinedPdf = async () => {
    if (!aggregate.perUser.length) return;
    const brandingAssets = await loadLogbuchPdfBrandingAssets();
    const doc = buildCombinedPdf(
      aggregate.perUser,
      aggregate.totals,
      filters.year,
      aggregate.filters || {},
      progressByUser,
      brandingAssets,
      keywordOverview,
      keywordClusterTree,
    );
    pdfMake.createPdf(doc).download(buildLogbuchCombinedFilename({ year: filters.year }));
  };

  const canEditUserEntries = canManageStatisticsForeignEntries(user);
  const activeFilterCount = [
    filters.selectedUserIds.length > 0,
    filters.selectedRoles.length > 0,
    filters.selectedResponsibleKeys.length > 0,
    filters.selectedEntryTypes.length > 0,
    filters.excludedUserIds.length > 0,
    filters.excludedEntryTypes.length > 0,
  ].filter(Boolean).length;

  const filterFieldSx = {
    '& .MuiOutlinedInput-root': {
      minHeight: 56,
      borderRadius: 18,
      bgcolor: 'rgba(255,255,255,0.94)',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
      '& fieldset': {
        borderColor: 'rgba(15, 23, 42, 0.12)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(15, 23, 42, 0.24)',
      },
      '&.Mui-focused': {
        bgcolor: '#fff',
        boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.08)',
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 600,
    },
  };

  const exportButtonSx = {
    minHeight: 46,
    borderRadius: 999,
    px: 2,
    fontWeight: 700,
    boxShadow: 'none',
    width: { xs: '100%', md: 'auto' },
  };

  const summaryCardSx = {
    height: '100%',
    borderRadius: '20px',
    border: 1,
    borderColor: 'rgba(15, 23, 42, 0.08)',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)',
    boxShadow: '0 12px 28px rgba(15, 23, 42, 0.08)',
  };

  const filterGroupCardSx = {
    borderRadius: '22px',
    border: 1,
    borderColor: 'rgba(15, 23, 42, 0.09)',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(246,248,251,0.92) 100%)',
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.045)',
    height: '100%',
  };

  const filterSectionEyebrowSx = {
    color: 'text.secondary',
    fontWeight: 800,
    letterSpacing: 1.1,
    lineHeight: 1,
    width: 'fit-content',
    px: 1,
    py: 0.55,
    borderRadius: 999,
    bgcolor: 'rgba(15, 23, 42, 0.05)',
  };

  const statusChipSx = {
    borderRadius: 999,
    fontWeight: 700,
    maxWidth: '100%',
    '& .MuiChip-label': {
      display: 'block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      px: { xs: 1.1, sm: 1.5 },
    },
  };

  const teaserChipSx = {
    borderRadius: 999,
    fontWeight: 600,
    maxWidth: '100%',
    bgcolor: 'rgba(255,255,255,0.74)',
    '& .MuiChip-label': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      px: { xs: 1, sm: 1.25 },
    },
  };

  const activeStatisticsTimeMode = filters.timeMode === 'unscaled' ? 'unscaled' : 'scaled';
  const summaryTiles = [
    {
      key: 'serviceTime',
      label: `Summe Dienstzeiten (${getStatisticsTimeModeLabel(activeStatisticsTimeMode)})`,
      value: aggregate.displayTotals?.serviceTime ?? aggregate.totals.serviceTime,
    },
    {
      key: 'hours',
      label: 'Summe HöRett Stunden',
      value: aggregate.totals.hours,
    },
    {
      key: 'prepTime',
      label: `Summe Vor-/Nachbereitung (${getStatisticsTimeModeLabel(activeStatisticsTimeMode)})`,
      value: aggregate.displayTotals?.prepTime ?? aggregate.totals.prepTime,
    },
    {
      key: 'pauseTime',
      label: `Summe Pausenzeiten (${getStatisticsTimeModeLabel(activeStatisticsTimeMode)})`,
      value: aggregate.displayTotals?.pauseTime ?? aggregate.totals.pauseTime,
    },
  ];

  const hasPersonFilters = filters.selectedUserIds.length > 0 || filters.excludedUserIds.length > 0 || filters.selectedRoles.length > 0 || filters.selectedWachbereitschaften.length > 0 || filters.selectedResponsibleKeys.length > 0;
  const hasActivityFilters = filters.selectedEntryTypes.length > 0 || filters.excludedEntryTypes.length > 0;
  const teaserItems = [
    { key: 'year', label: `Jahr ${filters.year}` },
    { key: 'time-mode', label: filters.timeMode === 'scaled' ? 'Tagesdienst: skaliert' : 'Tagesdienst: unskaliert' },
    { key: 'group', label: filters.groupBy === 'wachbereitschaft' ? 'Nach Wachbereitschaft' : 'Keine Gruppierung' },
    { key: 'sort', label: `Sortierung: ${getStatisticsSortLabel(filters.sortKey)}`, hideOnMobile: true },
    hasPersonFilters && hasActivityFilters
      ? { key: 'scope', label: 'Personen & Tätigkeiten gefiltert', hideOnMobile: true }
      : hasPersonFilters
        ? { key: 'scope', label: 'Personalfokus eingeschränkt', hideOnMobile: true }
        : hasActivityFilters
          ? { key: 'scope', label: 'Tätigkeiten gefiltert', hideOnMobile: true }
          : null,
  ].filter(Boolean);

  const selectedUserSummary = formatCompactSelection(
    filters.selectedUserIds.map((id) => usersById.get(Number(id))?.full_name),
    'Alle aktiven Benutzer',
    'Benutzer gewählt',
  );

  const excludedUserSummary = formatCompactSelection(
    filters.excludedUserIds.map((id) => usersById.get(Number(id))?.full_name),
    'Keine Benutzer ausgeblendet',
    'Benutzer ausgeblendet',
  );

  const selectedRoleSummary = formatCompactSelection(
    filters.selectedRoles.map((role) => ROLE_LABELS[role] || role),
    'Alle Rollen',
    'Rollen gewählt',
  );

  const selectedWachbereitschaftSummary = formatCompactSelection(
    filters.selectedWachbereitschaften.map((value) => (value === 'none' ? 'Ohne Zuordnung' : WACHBEREITSCHAFT_LABELS[value] || value)),
    'Alle Wachbereitschaften',
    'Wachbereitschaften gewählt',
  );

  const selectedResponsibleSummary = formatCompactSelection(
    filters.selectedResponsibleKeys.map((id) => responsiblesById.get(String(id))?.full_name),
    'Alle Verantwortlichen',
    'Verantwortliche gewählt',
  );

  const selectedEntryTypeSummary = formatCompactSelection(
    filters.selectedEntryTypes,
    'Alle Tätigkeiten',
    'Tätigkeiten gewählt',
  );

  const excludedEntryTypeSummary = formatCompactSelection(
    filters.excludedEntryTypes,
    'Keine Tätigkeiten ausgeblendet',
    'Tätigkeiten ausgeblendet',
  );

  return (
    <Box sx={{ px: { xs: 2, md: 5 }, py: 2.5 }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Statistik
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}
        {saveMessage && <Alert severity="success">{saveMessage}</Alert>}

        <Accordion
          expanded={filterPanelExpanded}
          onChange={(_, expanded) => setFilterPanelExpanded(expanded)}
          disableGutters
          sx={{
            borderRadius: '24px',
            border: 1,
            borderColor: 'rgba(15, 23, 42, 0.08)',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.94) 100%)',
            boxShadow: '0 18px 40px rgba(15, 23, 42, 0.08)',
            backdropFilter: 'blur(8px)',
            overflow: 'hidden',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: { xs: 1.5, md: 2.5 }, py: { xs: 1, md: 1.25 } }}>
            <Stack spacing={1.5} sx={{ width: '100%' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Box>
                  <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: 1.1 }}>
                    Filter & Export
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                  <Chip
                    label={(
                      <>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                          {activeFilterCount > 0 ? `${activeFilterCount} aktiv` : 'Keine Filter'}
                        </Box>
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                          {activeFilterCount > 0 ? `${activeFilterCount} Filter aktiv` : 'Keine Zusatzfilter aktiv'}
                        </Box>
                      </>
                    )}
                    color={activeFilterCount > 0 ? 'primary' : 'default'}
                    variant={activeFilterCount > 0 ? 'filled' : 'outlined'}
                    sx={statusChipSx}
                  />
                  <Chip
                    label={(
                      <>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                          {compactPreferenceStatusLabel}
                        </Box>
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                          {preferenceStatusLabel}
                        </Box>
                      </>
                    )}
                    color={preferenceSaveState === 'error' ? 'error' : 'default'}
                    variant="outlined"
                    sx={{ ...statusChipSx, fontWeight: 600, maxWidth: { xs: '185px', sm: '100%' } }}
                  />
                </Stack>
              </Stack>

              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {teaserItems.map((item, index) => (
                  <Chip
                    key={item.key}
                    label={item.label}
                    size="small"
                    variant="outlined"
                    sx={{
                      ...teaserChipSx,
                      display: item.hideOnMobile || index >= 2 ? { xs: 'none', sm: 'inline-flex' } : 'inline-flex',
                    }}
                  />
                ))}
              </Stack>
            </Stack>
          </AccordionSummary>

          <AccordionDetails sx={{ px: { xs: 1.5, md: 2.5 }, pb: { xs: 1.5, md: 2.5 }, pt: 0 }}>
            <Stack spacing={{ xs: 1.5, md: 2 }}>
              <Card variant="outlined" sx={filterGroupCardSx}>
                <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={{ xs: 1.5, md: 2 }}>
                    <Stack spacing={0.6}>
                      <Typography variant="overline" sx={filterSectionEyebrowSx}>
                        Basisansicht
                      </Typography>
                    </Stack>

                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={3}>
                        <Stack spacing={0.75}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                            Jahr
                          </Typography>
                          <FormControl fullWidth size="small" sx={filterFieldSx}>
                            <InputLabel id="stats-year-label">Jahr</InputLabel>
                            <Select
                              labelId="stats-year-label"
                              label="Jahr"
                              value={filters.year}
                              onChange={(event) => updateFilters({ year: Number(event.target.value) })}
                            >
                              {Array.from({ length: 6 }, (_, index) => currentYear - index).map((year) => (
                                <MenuItem key={year} value={year}>{year}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Stack>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <Stack spacing={0.75}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                            Zeitmodus
                          </Typography>
                          <FormControl fullWidth size="small" sx={filterFieldSx}>
                            <InputLabel id="stats-time-mode-label">Zeitmodus</InputLabel>
                            <Select
                              labelId="stats-time-mode-label"
                              label="Zeitmodus"
                              value={filters.timeMode}
                              onChange={(event) => updateFilters({ timeMode: event.target.value })}
                            >
                              <MenuItem value="scaled">Skaliert (Tagesdienst/Honorar)</MenuItem>
                              <MenuItem value="unscaled">Unskaliert (Ist-Zeit)</MenuItem>
                            </Select>
                          </FormControl>
                        </Stack>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <Stack spacing={0.75}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                            Gruppierung
                          </Typography>
                          <FormControl fullWidth size="small" sx={filterFieldSx}>
                            <InputLabel id="stats-group-label">Gruppierung</InputLabel>
                            <Select
                              labelId="stats-group-label"
                              label="Gruppierung"
                              value={filters.groupBy}
                              onChange={(event) => updateFilters({ groupBy: event.target.value })}
                            >
                              <MenuItem value="none">Keine Gruppierung</MenuItem>
                              <MenuItem value="wachbereitschaft">Nach Wachbereitschaft</MenuItem>
                            </Select>
                          </FormControl>
                        </Stack>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <Stack spacing={0.75}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                            Sortierung innerhalb der Auswahl
                          </Typography>
                          <FormControl fullWidth size="small" sx={filterFieldSx}>
                            <InputLabel id="stats-sort-label">Sortierung</InputLabel>
                            <Select
                              labelId="stats-sort-label"
                              label="Sortierung"
                              value={filters.sortKey}
                              onChange={(event) => updateFilters({ sortKey: event.target.value })}
                            >
                              <MenuItem value="needs-attention">Handlungsbedarf zuerst</MenuItem>
                              <MenuItem value="name-asc">Name A-Z</MenuItem>
                              <MenuItem value="name-desc">Name Z-A</MenuItem>
                              <MenuItem value="total-progress-desc">Gesamtfortschritt absteigend</MenuItem>
                              <MenuItem value="trainer-progress-desc">Lehrhöhenretterstunden absteigend</MenuItem>
                              <MenuItem value="duplicates-open-desc">Offene Duplikate zuerst</MenuItem>
                              <MenuItem value="last-entry-desc">Letzter Eintrag zuerst</MenuItem>
                            </Select>
                          </FormControl>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Stack>
                </CardContent>
              </Card>

              <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                  <Card variant="outlined" sx={filterGroupCardSx}>
                    <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                      <Stack spacing={{ xs: 1.5, md: 2 }}>
                        <Stack spacing={0.6}>
                          <Typography variant="overline" sx={filterSectionEyebrowSx}>
                            Personalfokus
                          </Typography>
                        </Stack>

                        <Grid container spacing={1.5}>
                          <Grid item xs={12}>
                            <Stack spacing={0.75}>
                              <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                                Benutzer einblenden
                              </Typography>
                              <FormControl fullWidth size="small" sx={filterFieldSx}>
                                <InputLabel id="stats-user-label">Benutzer</InputLabel>
                                <Select
                                  multiple
                                  labelId="stats-user-label"
                                  value={filters.selectedUserIds}
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    updateFilters({ selectedUserIds: typeof value === 'string' ? value.split(',').map(Number) : value.map(Number) });
                                  }}
                                  input={<OutlinedInput label="Benutzer" />}
                                  renderValue={() => selectedUserSummary}
                                  MenuProps={menuProps}
                                >
                                  {filterOptions.users.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                      <Checkbox checked={filters.selectedUserIds.includes(Number(option.id))} />
                                      <ListItemText primary={option.full_name} secondary={formatWachbereitschaft(option.wachbereitschaft)} />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Stack>
                          </Grid>

                          <Grid item xs={12}>
                            <Stack spacing={0.75}>
                              <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                                Rollen einbeziehen
                              </Typography>
                              <FormControl fullWidth size="small" sx={filterFieldSx}>
                                <InputLabel id="stats-role-label">Rollen</InputLabel>
                                <Select
                                  multiple
                                  labelId="stats-role-label"
                                  value={filters.selectedRoles}
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    updateFilters({ selectedRoles: typeof value === 'string' ? value.split(',') : value.map(String) });
                                  }}
                                  input={<OutlinedInput label="Rollen" />}
                                  renderValue={() => selectedRoleSummary}
                                  MenuProps={menuProps}
                                >
                                  {filterOptions.roles.map((role) => (
                                    <MenuItem key={role} value={role}>
                                      <Checkbox checked={filters.selectedRoles.includes(role)} />
                                      <ListItemText primary={ROLE_LABELS[role] || role} />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Stack>
                          </Grid>

                          <Grid item xs={12}>
                            <Stack spacing={0.75}>
                              <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                                Wachbereitschaft / Zugehörigkeit
                              </Typography>
                              <FormControl fullWidth size="small" sx={filterFieldSx}>
                                <InputLabel id="stats-wachbereitschaft-label">Wachbereitschaft</InputLabel>
                                <Select
                                  multiple
                                  labelId="stats-wachbereitschaft-label"
                                  value={filters.selectedWachbereitschaften}
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    updateFilters({ selectedWachbereitschaften: typeof value === 'string' ? value.split(',') : value.map(String) });
                                  }}
                                  input={<OutlinedInput label="Wachbereitschaft" />}
                                  renderValue={() => selectedWachbereitschaftSummary}
                                  MenuProps={menuProps}
                                >
                                  {WACHBEREITSCHAFT_FILTER_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                      <Checkbox checked={filters.selectedWachbereitschaften.includes(option.value)} />
                                      <ListItemText primary={option.label} />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Stack>
                          </Grid>

                          <Grid item xs={12}>
                            <Stack spacing={0.75}>
                              <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                                Benutzer ausblenden
                              </Typography>
                              <FormControl fullWidth size="small" sx={filterFieldSx}>
                                <InputLabel id="stats-user-exclude-label">Ausgeblendete Benutzer</InputLabel>
                                <Select
                                  multiple
                                  labelId="stats-user-exclude-label"
                                  value={filters.excludedUserIds}
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    updateFilters({ excludedUserIds: typeof value === 'string' ? value.split(',').map(Number) : value.map(Number) });
                                  }}
                                  input={<OutlinedInput label="Ausgeblendete Benutzer" />}
                                  renderValue={() => excludedUserSummary}
                                  MenuProps={menuProps}
                                >
                                  {filterOptions.users.map((option) => (
                                    <MenuItem key={`exclude-${option.id}`} value={option.id}>
                                      <Checkbox checked={filters.excludedUserIds.includes(Number(option.id))} />
                                      <ListItemText primary={option.full_name} secondary={(option.roles || []).includes('admin') ? 'Admin' : formatWachbereitschaft(option.wachbereitschaft)} />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Stack>
                          </Grid>

                          <Grid item xs={12}>
                            <Stack spacing={0.75}>
                              <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                                Verantwortlicher
                              </Typography>
                              <FormControl fullWidth size="small" sx={filterFieldSx}>
                                <InputLabel id="stats-responsible-label">Verantwortlicher</InputLabel>
                                <Select
                                  multiple
                                  labelId="stats-responsible-label"
                                  label="Verantwortlicher"
                                  value={filters.selectedResponsibleKeys}
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    updateFilters({ selectedResponsibleKeys: typeof value === 'string' ? value.split(',') : value.map(String) });
                                  }}
                                  input={<OutlinedInput label="Verantwortlicher" />}
                                  renderValue={() => selectedResponsibleSummary}
                                  MenuProps={menuProps}
                                >
                                  {filterOptions.responsibles.map((resp) => (
                                    <MenuItem key={resp.id} value={resp.id}>
                                      <Checkbox checked={filters.selectedResponsibleKeys.includes(String(resp.id))} />
                                      <ListItemText primary={resp.full_name} />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Stack>
                          </Grid>
                        </Grid>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Card variant="outlined" sx={filterGroupCardSx}>
                    <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                      <Stack spacing={{ xs: 1.5, md: 2 }}>
                        <Stack spacing={0.6}>
                          <Typography variant="overline" sx={filterSectionEyebrowSx}>
                            Tätigkeiten
                          </Typography>
                        </Stack>

                        <Grid container spacing={1.5}>
                          <Grid item xs={12}>
                            <Stack spacing={0.75}>
                              <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                                Tätigkeiten einbeziehen
                              </Typography>
                              <FormControl fullWidth size="small" sx={filterFieldSx}>
                                <InputLabel id="stats-entry-type-label">Tätigkeiten</InputLabel>
                                <Select
                                  multiple
                                  labelId="stats-entry-type-label"
                                  label="Tätigkeiten"
                                  value={filters.selectedEntryTypes}
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    updateFilters({ selectedEntryTypes: typeof value === 'string' ? value.split(',') : value });
                                  }}
                                  input={<OutlinedInput label="Tätigkeiten" />}
                                  renderValue={() => selectedEntryTypeSummary}
                                  MenuProps={menuProps}
                                >
                                  {filterOptions.entryTypes.map((type) => (
                                    <MenuItem key={type} value={type}>
                                      <Checkbox checked={filters.selectedEntryTypes.includes(type)} />
                                      <ListItemText primary={type} />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Stack>
                          </Grid>

                          <Grid item xs={12}>
                            <Stack spacing={0.75}>
                              <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                                Tätigkeiten ausblenden
                              </Typography>
                              <FormControl fullWidth size="small" sx={filterFieldSx}>
                                <InputLabel id="stats-entry-type-exclude-label">Ausgeblendete Tätigkeiten</InputLabel>
                                <Select
                                  multiple
                                  labelId="stats-entry-type-exclude-label"
                                  label="Ausgeblendete Tätigkeiten"
                                  value={filters.excludedEntryTypes}
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    updateFilters({ excludedEntryTypes: typeof value === 'string' ? value.split(',') : value });
                                  }}
                                  input={<OutlinedInput label="Ausgeblendete Tätigkeiten" />}
                                  renderValue={() => excludedEntryTypeSummary}
                                  MenuProps={menuProps}
                                >
                                  {filterOptions.entryTypes.map((type) => (
                                    <MenuItem key={`exclude-${type}`} value={type}>
                                      <Checkbox checked={filters.excludedEntryTypes.includes(type)} />
                                      <ListItemText primary={type} />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Stack>
                          </Grid>
                        </Grid>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Card variant="outlined" sx={filterGroupCardSx}>
                <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={{ xs: 1.5, md: 2 }}>
                    <Stack spacing={0.6}>
                      <Typography variant="overline" sx={filterSectionEyebrowSx}>
                        Export
                      </Typography>
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} justifyContent={{ xs: 'stretch', md: 'flex-start' }}>
                      <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={handleExportSinglePdfs} disabled={!aggregate.perUser.length} sx={exportButtonSx}>
                        PDF je User
                      </Button>
                      <Button variant="contained" startIcon={<PictureAsPdfIcon />} onClick={handleExportCombinedPdf} disabled={!aggregate.perUser.length} sx={exportButtonSx}>
                        Sammel-PDF
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Grid container spacing={2}>
          {summaryTiles.map((tile) => (
            <Grid key={tile.key} item xs={12} md={6} lg={3}>
              <Card sx={summaryCardSx}>
                <CardContent sx={{ p: 2.25 }}>
                  <Stack spacing={1}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
                      {tile.label}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                      {formatHours(tile.value || 0)}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Accordion
          expanded={keywordPanelExpanded}
          onChange={(event, expanded) => {
            if (event?.target?.closest?.('[data-accordion-action="keyword-undo"]')) {
              return;
            }
            if (keywordEditorDragging) {
              return;
            }
            setKeywordPanelExpanded(expanded);
          }}
          disableGutters
          sx={{
            borderRadius: '18px',
            border: 1,
            borderColor: 'rgba(15, 23, 42, 0.08)',
            overflow: 'hidden',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: { xs: 1.25, sm: 1.5, md: 2 }, py: { xs: 0.75, md: 1 } }}>
            <Stack spacing={1} sx={{ width: '100%', minWidth: 0 }}>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', lg: 'center' }}>
                <Stack spacing={0.4} sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Schlagwörter & Cluster
                  </Typography>
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ width: { xs: '100%', lg: 'auto' } }}>
                  {keywordClusterTree.canManage && (
                    <Button
                      data-accordion-action="keyword-undo"
                      size="small"
                      variant="text"
                      sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
                      onMouseDown={(event) => {
                        event.stopPropagation();
                      }}
                      onTouchStart={(event) => {
                        event.stopPropagation();
                      }}
                      onKeyDown={(event) => {
                        event.stopPropagation();
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleUndoLastKeywordTreeAction();
                      }}
                      onFocusCapture={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      Letzte Aktion rückgängig
                    </Button>
                  )}

                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    <Chip size="small" variant="outlined" label={`Rohbegriffe: ${keywordOverview.rawKeywords.length}`} />
                    <Chip size="small" variant="outlined" label={`Kategorien: ${keywordClusterTree.nodes.length}`} />
                    <Chip size="small" variant="outlined" label={`Verbindungen: ${keywordClusterTree.edges.length}`} />
                    <Chip size="small" variant="outlined" label={`Zuordnungen: ${keywordClusterTree.mappings.length}`} />
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </AccordionSummary>

          <AccordionDetails sx={{ px: { xs: 1.25, sm: 1.5, md: 2 }, pb: 2, pt: 0 }}>
            {keywordLoading ? (
              <Stack direction="row" spacing={1.2} alignItems="center" sx={{ py: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">Schlagwortdaten werden geladen...</Typography>
              </Stack>
            ) : (
              <Stack spacing={1.5}>
                <KeywordClusterEditor
                  canManage={keywordClusterTree.canManage}
                  tree={keywordClusterTree}
                  onDragStateChange={setKeywordEditorDragging}
                  onCreateTopic={async (payload) => {
                    try {
                      setError('');
                      await handleEditorCreateTopic(payload);
                    } catch (editorError) {
                      setError(editorError.response?.data?.error || 'Kategorie konnte nicht erstellt werden.');
                    }
                  }}
                  onUpdateTopic={async (topicId, payload) => {
                    try {
                      setError('');
                      await handleEditorUpdateTopic(topicId, payload);
                    } catch (editorError) {
                      setError(editorError.response?.data?.error || 'Kategorie konnte nicht aktualisiert werden.');
                    }
                  }}
                  onDeleteTopic={async (topicId) => {
                    try {
                      setError('');
                      await handleEditorDeleteTopic(topicId);
                    } catch (editorError) {
                      setError(editorError.response?.data?.error || 'Kategorie konnte nicht gelöscht werden.');
                    }
                  }}
                  onCreateEdge={async (sourceNodeId, targetNodeId) => {
                    try {
                      setError('');
                      await handleEditorCreateEdge(sourceNodeId, targetNodeId);
                    } catch (editorError) {
                      setError(editorError.response?.data?.error || 'Verbindung konnte nicht erstellt werden.');
                    }
                  }}
                  onDeleteEdge={async (edgeId) => {
                    try {
                      setError('');
                      await handleEditorDeleteEdge(edgeId);
                    } catch (editorError) {
                      setError(editorError.response?.data?.error || 'Verbindung konnte nicht entfernt werden.');
                    }
                  }}
                  onAddMappings={async (topicId, normalizedKeywords) => {
                    try {
                      setError('');
                      await handleEditorAddMappings(topicId, normalizedKeywords);
                    } catch (editorError) {
                      setError(editorError.response?.data?.error || 'Zuordnungen konnten nicht gespeichert werden.');
                    }
                  }}
                  onRemoveMappings={async (topicId, normalizedKeywords) => {
                    try {
                      setError('');
                      await handleEditorRemoveMappings(topicId, normalizedKeywords);
                    } catch (editorError) {
                      setError(editorError.response?.data?.error || 'Zuordnungen konnten nicht entfernt werden.');
                    }
                  }}
                />

                {keywordClusterTree.canManage && keywordClusterTree.history.length > 0 && (
                  <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
                    <CardContent sx={{ p: 1.5 }}>
                      <Stack spacing={1}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Letzte Baum-Aktionen</Typography>
                        <Stack spacing={0.5}>
                          {keywordClusterTree.history.slice(0, 8).map((row) => (
                            <Typography key={`cluster-history-${row.id}`} variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
                              {`${formatKeywordClusterHistoryItem(row)} (${formatDateTime(row.createdAt)})`}
                            </Typography>
                          ))}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                )}

              </Stack>
            )}
          </AccordionDetails>
        </Accordion>

        <Stack spacing={2}>
          {visibleProgressGroups.map((group) => (
            <Stack key={group.key} spacing={1.5}>
              {group.label && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 0.5 }}>
                  <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.8 }}>
                    {group.label}
                  </Typography>
                  <Chip size="small" variant="outlined" label={`${group.rows.length} Benutzer`} />
                </Stack>
              )}

              {group.rows.map((row) => {
            const detail = detailByUserId[row.userId];
            const isExpanded = expandedUserId === row.userId;
            const isLoadingDetail = Boolean(detailLoadingByUserId[row.userId]);
            const detailError = detailErrorByUserId[row.userId];
            const complianceLights = sortComplianceLights(row.complianceLights || []);

            return (
              <Accordion
                key={row.userId}
                expanded={isExpanded}
                onChange={(_, expanded) => handleToggleAccordion(row.userId, expanded)}
                disableGutters
                sx={{
                  borderRadius: '20px !important',
                  border: 1,
                  borderColor: 'divider',
                  overflow: 'hidden',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: { xs: 1.1, sm: 1.5, md: 2 },
                    py: { xs: 0.85, md: 1 },
                    '@media (max-width:360px)': {
                      px: 0.9,
                      py: 0.7,
                    },
                    '& .MuiAccordionSummary-content': {
                      minWidth: 0,
                    },
                  }}
                >
                  <Stack spacing={{ xs: 0.8, md: 0.85 }} sx={{ width: '100%', minWidth: 0 }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: 'minmax(0, 1fr) 137px',
                          sm: 'minmax(0, 1fr) 157px auto',
                          md: 'minmax(220px, 1fr) minmax(118px, 132px) minmax(160px, 210px) minmax(145px, 185px) 157px minmax(110px, 140px)',
                        },
                        gridTemplateAreas: {
                          xs: '"identity status" "compliance compliance" "progress progress" "metrics metrics" "lastEntry lastEntry"',
                          sm: '"identity status lastEntry" "compliance compliance compliance" "progress progress progress" "metrics metrics metrics"',
                          md: '"identity compliance progress metrics status lastEntry"',
                        },
                        alignItems: 'center',
                        columnGap: { xs: 0.8, sm: 1, md: 1.1 },
                        rowGap: { xs: 0.55, sm: 0.65, md: 0 },
                        minWidth: 0,
                        '@media (max-width:360px)': {
                          columnGap: 0.55,
                          rowGap: 0.4,
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={0.45}
                        alignItems="center"
                        sx={{ gridArea: 'identity', minWidth: 0, overflow: 'hidden' }}
                      >
                        {formatWachbereitschaft(row.wachbereitschaft) && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, flexShrink: 0 }}>
                            {formatWachbereitschaft(row.wachbereitschaft)}
                          </Typography>
                        )}
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            lineHeight: 1.2,
                            minWidth: 0,
                            flex: '1 1 auto',
                            fontSize: { xs: '0.82rem', sm: '0.875rem' },
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            '@media (max-width:360px)': {
                              fontSize: '0.79rem',
                              lineHeight: 1.15,
                            },
                          }}
                          title={row.full_name}
                        >
                          {row.full_name}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          sx={{ width: 50, flex: '0 0 50px' }}
                        >
                          <Tooltip title="50:50-Selbstreflexion anzeigen">
                            <IconButton
                              size="small"
                              aria-label={`50:50-Selbstreflexion für ${row.full_name} anzeigen`}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                setSelfReflectionQuickView({ id: Number(row.userId), fullName: row.full_name });
                              }}
                              onFocus={(event) => event.stopPropagation()}
                              sx={{ width: 28, height: 28, p: 0.5, flexShrink: 0 }}
                            >
                              <CompareArrowsIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Box sx={{ width: 18, height: 18, flexShrink: 0 }}>
                            {savedNotesByUserId[row.userId] && (
                              <Tooltip title="Bemerkung vorhanden">
                                <NotesIcon color="info" sx={{ display: 'block', fontSize: 18 }} />
                              </Tooltip>
                            )}
                          </Box>
                        </Stack>
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={0.45}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{
                          gridArea: 'compliance',
                          minHeight: 22,
                          minWidth: 0,
                          maxWidth: '100%',
                        }}
                      >
                        {complianceLights.map((light) => {
                          const palette = getComplianceLightPalette(light);

                          return (
                            <Box
                              key={`${row.userId}-${light.key || light.label}`}
                              title={light.label}
                              sx={{
                                minWidth: { xs: 24, md: 26 },
                                height: { xs: 17, md: 18 },
                                px: { xs: 0.45, md: 0.55 },
                                borderRadius: 999,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: palette.bgColor,
                                color: palette.textColor,
                                border: 1,
                                borderColor: palette.borderColor,
                                fontSize: { xs: 9, md: 10 },
                                fontWeight: 800,
                                lineHeight: 1,
                                flexShrink: 0,
                                '@media (max-width:360px)': {
                                  minWidth: 22,
                                  height: 16,
                                  px: 0.38,
                                  fontSize: 8.5,
                                },
                              }}
                            >
                              {getComplianceShortLabel(light)}
                            </Box>
                          );
                        })}
                      </Stack>

                      <Box sx={{ gridArea: 'progress', minWidth: 0, width: '100%' }}>
                        <Stack spacing={0.45}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, Number(row.total_progress || 0))}
                            sx={{
                              height: { xs: 4.5, md: 5 },
                              borderRadius: 99,
                              bgcolor: 'rgba(15, 23, 42, 0.12)',
                              '@media (max-width:360px)': {
                                height: 4,
                              },
                            }}
                          />
                          <LinearProgress
                            color="success"
                            variant="determinate"
                            value={Math.min(100, Number(row.trainer_progress || 0))}
                            sx={{
                              height: { xs: 4.5, md: 5 },
                              borderRadius: 99,
                              bgcolor: 'rgba(15, 23, 42, 0.12)',
                              '@media (max-width:360px)': {
                                height: 4,
                              },
                            }}
                          />
                        </Stack>
                      </Box>

                      <Box sx={{ gridArea: 'metrics', minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            display: { xs: 'none', sm: 'block' },
                            fontWeight: 700,
                            fontSize: { sm: '0.76rem', md: '0.82rem' },
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {`G ${formatHoursCompact(row.progress_total_hours ?? row.total_hours)}/${formatHoursCompact(row.requirements?.total || 72)} | L ${formatHoursCompact(row.trainer_hours)}/${formatHoursCompact(row.requirements?.trainer || 54)}`}
                        </Typography>
                        <Stack spacing={0.1} sx={{ display: { xs: 'flex', sm: 'none' } }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '0.72rem' }}>
                            {`G ${formatHoursCompact(row.progress_total_hours ?? row.total_hours)}/${formatHoursCompact(row.requirements?.total || 72)}`}
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '0.72rem' }}>
                            {`L ${formatHoursCompact(row.trainer_hours)}/${formatHoursCompact(row.requirements?.trainer || 54)}`}
                          </Typography>
                        </Stack>
                      </Box>

                      <Stack
                        direction="row"
                        sx={{
                          gridArea: 'status',
                          display: 'grid',
                          gridTemplateColumns: { xs: '112px 20px', sm: '128px 24px' },
                          columnGap: { xs: 0.6, sm: 0.625 },
                          alignItems: 'center',
                          width: { xs: 137, sm: 157 },
                          minHeight: 24,
                          justifySelf: 'end',
                        }}
                      >
                        {(row.pendingChangeRequestCount || 0) > 0 && (
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label={`${row.pendingChangeRequestCount} ${row.pendingChangeRequestCount === 1 ? 'Anfrage' : 'Anfragen'} offen`}
                            sx={{
                              gridColumn: 1,
                              height: { xs: 22, sm: 24 },
                              width: { xs: 112, sm: 128 },
                              fontWeight: 700,
                              '& .MuiChip-label': {
                                px: { xs: 0.7, sm: 1 },
                                fontSize: { xs: '0.64rem', sm: '0.7rem' },
                              },
                            }}
                          />
                        )}
                        {(row.duplicateSummary?.open || 0) > 0 && (
                          <Chip
                            size="small"
                            color="warning"
                            icon={<WarningAmberIcon sx={{ fontSize: '15px !important' }} />}
                            label=""
                            aria-label={`${row.duplicateSummary.open} offene Duplikate`}
                            sx={{
                              gridColumn: 2,
                              height: { xs: 22, sm: 24 },
                              width: { xs: 22, sm: 24 },
                              borderRadius: '50%',
                              p: 0,
                              '@media (max-width:360px)': {
                                height: 20,
                                width: 20,
                              },
                              '& .MuiChip-label': {
                                display: 'none',
                                p: 0,
                              },
                              '& .MuiChip-icon': {
                                margin: 0,
                              },
                            }}
                          />
                        )}
                      </Stack>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          gridArea: 'lastEntry',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          justifySelf: { xs: 'start', sm: 'end' },
                          textAlign: { xs: 'left', sm: 'right' },
                          fontSize: { xs: '0.68rem', md: '0.72rem' },
                          '@media (max-width:360px)': {
                            fontSize: '0.63rem',
                          },
                        }}
                      >
                        Eintrag: {formatDate(row.last_entry_date)}
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails sx={{ px: 2.25, pb: 2.25, pt: 0 }}>
                  <Divider sx={{ mb: 2 }} />

                  {isLoadingDetail && !detail && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        Benutzer-Logbuch wird geladen...
                      </Typography>
                    </Stack>
                  )}

                  {detailError && <Alert severity="error" sx={{ mb: 2 }}>{detailError}</Alert>}

                  {detail && (
                    <Stack spacing={2}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Card variant="outlined" sx={{ borderRadius: 3 }}>
                            <CardContent>
                              <Stack spacing={1.25}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                  Bemerkung {filters.year}
                                </Typography>
                                <TextField
                                  multiline
                                  minRows={4}
                                  value={draftNotes[row.userId] || ''}
                                  onChange={(event) => setDraftNotes((prev) => ({ ...prev, [row.userId]: event.target.value }))}
                                  placeholder="Bemerkung zu diesem Benutzerjahr"
                                />
                                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                  <Typography variant="caption" color="text.secondary">
                                    {detail.note
                                      ? `Zuletzt aktualisiert: ${formatDateTime(detail.note.updated_at)} durch ${detail.note.updated_by_name || '-'}`
                                      : 'Noch keine Bemerkung gespeichert.'}
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={() => handleSaveNote(row.userId)}
                                    disabled={Boolean(noteSavingByUserId[row.userId])}
                                  >
                                    {noteSavingByUserId[row.userId] ? 'Speichert...' : 'Bemerkung speichern'}
                                  </Button>
                                </Stack>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>

                      {(detail.duplicates || []).length > 0 && (
                        <Card variant="outlined" sx={{ borderRadius: 3 }}>
                          <CardContent>
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              spacing={1}
                            >
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                  Duplikatsprüfung
                                </Typography>
                                <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }}>
                                  <Chip size="small" color={(detail.duplicates || []).some((item) => !item.reviewed) ? 'warning' : 'success'} label={`Offen ${(detail.duplicates || []).filter((item) => !item.reviewed).length}`} />
                                  <Chip size="small" variant="outlined" label={`Geprüft ${(detail.duplicates || []).filter((item) => item.reviewed).length}`} />
                                </Stack>
                              </Box>
                              <Tooltip title={duplicatePanelExpandedByUserId[row.userId] ? 'Duplikatsprüfung minimieren' : 'Duplikatsprüfung anzeigen'}>
                                <IconButton
                                  onClick={() => setDuplicatePanelExpandedByUserId((prev) => ({
                                    ...prev,
                                    [row.userId]: !prev[row.userId],
                                  }))}
                                  aria-label={duplicatePanelExpandedByUserId[row.userId] ? 'Duplikatsprüfung minimieren' : 'Duplikatsprüfung anzeigen'}
                                  aria-expanded={Boolean(duplicatePanelExpandedByUserId[row.userId])}
                                >
                                  {duplicatePanelExpandedByUserId[row.userId] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </Tooltip>
                            </Stack>
                            <Collapse in={Boolean(duplicatePanelExpandedByUserId[row.userId])} timeout="auto" unmountOnExit>
                              <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                                {(detail.duplicates || []).map((duplicate) => (
                                  <Card key={duplicate.duplicateKey} variant="outlined" sx={{ borderRadius: 2, bgcolor: duplicate.reviewed ? 'action.hover' : 'warning.50' }}>
                                    <CardContent>
                                      <Stack spacing={1}>
                                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                            {formatDate(duplicate.date)} | {formatHours(duplicate.hours)} | {duplicate.entryIds.map((entryId) => `Eintrag ${entryId}`).join(', ')}
                                          </Typography>
                                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                            <Chip size="small" label={duplicate.reviewed ? 'Geprüft' : 'Offen'} color={duplicate.reviewed ? 'success' : 'warning'} />
                                            {duplicate.followUp && (
                                              <Chip size="small" color="info" icon={<MarkEmailReadIcon />} label="Änderung angefragt" />
                                            )}
                                          </Stack>
                                        </Stack>
                                        {duplicate.followUp ? (
                                          <Stack spacing={0.75}>
                                            <Typography variant="caption" color="text.secondary">
                                              {`Änderung angefragt am ${formatDateTime(duplicate.followUp.markedAt)} durch ${duplicate.followUp.markedByName || '-'}`}
                                            </Typography>
                                            <Box>
                                              <Button
                                                size="small"
                                                color="error"
                                                variant="text"
                                                startIcon={<DeleteOutlineIcon />}
                                                onClick={() => handleRemoveDuplicateFollowUp(row.userId, duplicate.duplicateKey)}
                                                disabled={duplicateFollowUpBusyKey === duplicate.duplicateKey}
                                              >
                                                Aufforderung zur Logbuchänderung entfernen
                                              </Button>
                                            </Box>
                                          </Stack>
                                        ) : (
                                          <Box>
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              startIcon={<MarkEmailReadIcon />}
                                              onClick={() => handleMarkDuplicateFollowUp(row.userId, duplicate.duplicateKey)}
                                              disabled={duplicateFollowUpBusyKey === duplicate.duplicateKey}
                                            >
                                              Zur Logbuchänderung aufgefordert
                                            </Button>
                                          </Box>
                                        )}
                                        {!duplicate.reviewed && (
                                          <Box>
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              startIcon={<RuleIcon />}
                                              onClick={() => handleReviewDuplicate(row.userId, duplicate.duplicateKey)}
                                            >
                                              Als geprüft markieren
                                            </Button>
                                          </Box>
                                        )}
                                      </Stack>
                                    </CardContent>
                                  </Card>
                                ))}
                              </Stack>
                            </Collapse>
                          </CardContent>
                        </Card>
                      )}

                      <Card variant="outlined" sx={{ borderRadius: 3 }}>
                        <CardContent>
                          <Stack spacing={1.5}>
                            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                  Logbuch-Einträge
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {detail.entries?.length || 0} bestätigte Einträge im ausgewählten Zeitraum.
                                </Typography>
                                {canEditUserEntries && (detail.pendingEntries?.length || 0) > 0 && (
                                  <Typography variant="body2" color="warning.main">
                                    {detail.pendingEntries.length} noch nicht genehmigte Eintragsanfrage{detail.pendingEntries.length === 1 ? '' : 'n'}.
                                  </Typography>
                                )}
                              </Box>
                            </Stack>

                            {(detail.entries || []).length === 0 && (
                              <Typography variant="body2" color="text.secondary">
                                Keine bestätigten Einträge für die aktuelle Filterauswahl.
                              </Typography>
                            )}

                            {canEditUserEntries && (detail.pendingEntries || []).length > 0 && (
                              <Stack spacing={1.25}>
                                {(detail.pendingEntries || []).map((pendingEntry) => (
                                  <Card key={`pending-${pendingEntry.requestId}`} variant="outlined" sx={{ borderRadius: 2, borderColor: 'warning.main' }}>
                                    <CardContent>
                                      <Stack spacing={1.1}>
                                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                                            <Chip size="small" color="warning" label="Noch nicht genehmigt" />
                                            {pendingEntry.invalidPayload && <Chip size="small" color="error" variant="outlined" label="Unvollständige Altdaten" />}
                                            <Chip size="small" variant="outlined" label={`Anfrage ${pendingEntry.requestId}`} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{formatDate(pendingEntry.date)}</Typography>
                                            <Chip size="small" color="primary" label={formatHours(pendingEntry.hours)} />
                                            <Chip size="small" variant="outlined" label={pendingEntry.entryType || '-'} />
                                          </Stack>
                                          <Tooltip title="Anfrageaktionen">
                                            <IconButton
                                              size="small"
                                              aria-label={`Aktionen für offene Anfrage ${pendingEntry.requestId}`}
                                              onClick={(event) => setEntryActionMenu({ anchorEl: event.currentTarget, entry: pendingEntry, userId: row.userId, pendingCreate: true })}
                                            >
                                              <MoreVertIcon />
                                            </IconButton>
                                          </Tooltip>
                                        </Stack>
                                        <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
                                          <Typography variant="body2" color="text.secondary">Zeitraum: {pendingEntry.timeFrom && pendingEntry.timeTo ? `${pendingEntry.timeFrom} - ${pendingEntry.timeTo}` : '-'}</Typography>
                                          <Typography variant="body2" color="text.secondary">Verantwortlich: {pendingEntry.responsibleName || '-'}</Typography>
                                          <Typography variant="body2" color="text.secondary">Antragsteller: {pendingEntry.requestedByName || '-'}</Typography>
                                        </Stack>
                                        {pendingEntry.description && <Typography variant="body2">{pendingEntry.description}</Typography>}
                                      </Stack>
                                    </CardContent>
                                  </Card>
                                ))}
                              </Stack>
                            )}

                            <Stack spacing={1.25}>
                              {(detail.entries || []).map((entry) => {
                                const canEditThisEntry = Number(row.userId) === Number(user?.id) || canEditUserEntries;
                                const isSrhtBasicCourseEntry = entry.entry_type === 'Grundausbildungslehrgang SRHT';
                                const groupSize = Number(entry.entry_group_size || 1);
                                const entryKeywords = Array.isArray(entry.keywords) ? entry.keywords : [];
                                const visibleKeywords = expandedKeywordEntryIds[entry.id] ? entryKeywords : entryKeywords.slice(0, 3);
                                const hiddenKeywordCount = Math.max(0, entryKeywords.length - visibleKeywords.length);

                                return (
                                  <Card key={entry.id} variant="outlined" sx={{ borderRadius: 2 }}>
                                    <CardContent>
                                      <Stack spacing={1.1}>
                                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                                            <Chip size="small" variant="outlined" label={`Eintrag ${entry.id}`} sx={{ fontWeight: 700 }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                              {formatDate(entry.date)}
                                            </Typography>
                                            <Chip size="small" color="primary" label={formatHours(entry.hours)} />
                                            <Chip size="small" variant="outlined" label={entry.entry_type || '-'} />
                                            {entryKeywords.length > 0 && <Chip size="small" variant="outlined" label={`Themen: ${entryKeywords.length}`} />}
                                            {entry.created_by_user_id && Number(entry.created_by_user_id) !== Number(row.userId) && (
                                              <Chip size="small" color="warning" variant="outlined" label="Fremdeintrag" />
                                            )}
                                            {groupSize > 1 && <Chip size="small" variant="outlined" label={`Gruppe · ${groupSize} Einträge`} />}
                                            {entry.hasOpenDuplicate && <Chip size="small" color="warning" label="Offenes Duplikat" />}
                                            {entry.hasReviewedDuplicate && <Chip size="small" color="success" variant="outlined" label="Duplikat geprüft" />}
                                          </Stack>

                                          <Stack direction="row" spacing={1}>
                                            {canEditThisEntry && !isSrhtBasicCourseEntry && (
                                              <Tooltip title="Eintragsaktionen">
                                                <IconButton
                                                  size="small"
                                                  aria-label={`Aktionen für Eintrag ${entry.id}`}
                                                  onClick={(event) => setEntryActionMenu({ anchorEl: event.currentTarget, entry, userId: row.userId })}
                                                >
                                                  <MoreVertIcon />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                          </Stack>
                                        </Stack>

                                        <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
                                          <Typography variant="body2" color="text.secondary">Zeitraum: {entry.time_from && entry.time_to ? `${entry.time_from} - ${entry.time_to}` : '-'}</Typography>
                                          <Typography variant="body2" color="text.secondary">Pause ({getStatisticsTimeModeLabel(detail?.filters?.timeMode === 'unscaled' ? 'unscaled' : 'scaled')}): {formatHours(entry.display_pause_hours ?? entry.applied_pause_hours ?? entry.pause_hours ?? 0)}</Typography>
                                          <Typography variant="body2" color="text.secondary">Vor-/Nachbereitung ({getStatisticsTimeModeLabel(detail?.filters?.timeMode === 'unscaled' ? 'unscaled' : 'scaled')}): {formatHours(entry.display_prep_time ?? entry.prep_time ?? 0)}</Typography>
                                          <Typography variant="body2" color="text.secondary">Dienstzeit ({getStatisticsTimeModeLabel(detail?.filters?.timeMode === 'unscaled' ? 'unscaled' : 'scaled')}): {formatHours(entry.display_service_time ?? entry.service_time ?? 0)}</Typography>
                                          <Typography variant="body2" color="text.secondary">Verantwortlich: {entry.responsible_name || '-'}</Typography>
                                          <Typography variant="body2" color="text.secondary">Erstellt von: {entry.created_by_name || '-'}</Typography>
                                        </Stack>

                                        {entryKeywords.length > 0 && (
                                          <Stack spacing={0.75}>
                                            <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
                                              {visibleKeywords.map((keyword) => (
                                                <Chip
                                                  key={`stats-entry-keyword-${entry.id}-${keyword.id || keyword.raw}`}
                                                  size="small"
                                                  variant="outlined"
                                                  label={keyword.raw || keyword}
                                                />
                                              ))}
                                            </Stack>
                                            {(hiddenKeywordCount > 0 || entryKeywords.length > 3) && (
                                              <Box>
                                                <Button
                                                  size="small"
                                                  variant="text"
                                                  onClick={() => setExpandedKeywordEntryIds((prev) => ({
                                                    ...prev,
                                                    [entry.id]: !prev[entry.id],
                                                  }))}
                                                >
                                                  {expandedKeywordEntryIds[entry.id] ? 'Weniger anzeigen' : `Mehr anzeigen (${hiddenKeywordCount})`}
                                                </Button>
                                              </Box>
                                            )}
                                          </Stack>
                                        )}

                                      </Stack>
                                    </CardContent>
                                  </Card>
                                );
                              })}
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Stack>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
            </Stack>
          ))}

          {!visibleProgressRows.length && (
            <Alert severity="info">
              Keine Benutzer für die aktuelle Filterauswahl gefunden.
            </Alert>
          )}
        </Stack>
        <Menu
          anchorEl={entryActionMenu.anchorEl}
          open={Boolean(entryActionMenu.anchorEl)}
          onClose={() => setEntryActionMenu({ anchorEl: null, entry: null, userId: null })}
        >
          {entryActionMenu.pendingCreate ? (
            <MenuItem disabled={Boolean(entryActionMenu.entry?.invalidPayload)} onClick={() => openEntryRequestDialog('update', 'pending-create')}>Anfrage bearbeiten</MenuItem>
          ) : (
            <MenuItem onClick={() => openEntryRequestDialog('update', 'single')}>Eintrag bearbeiten</MenuItem>
          )}
          {entryActionMenu.pendingCreate ? (
            <MenuItem onClick={() => openEntryRequestDialog('cancel', 'pending-create')}>Anfrage zurückziehen</MenuItem>
          ) : (
            <MenuItem onClick={() => openEntryRequestDialog('delete', 'single')}>
              {Number(entryActionMenu.userId) === Number(user?.id) ? 'Eintrag löschen' : 'Löschung beantragen'}
            </MenuItem>
          )}
          {canEditUserEntries && (entryActionMenu.pendingCreate
            ? Boolean(entryActionMenu.entry?.groupAnchorEntryId)
            : Number(entryActionMenu.entry?.entry_group_size || 1) > 1) && <Divider />}
          {canEditUserEntries && (entryActionMenu.pendingCreate
            ? Boolean(entryActionMenu.entry?.groupAnchorEntryId)
            : Number(entryActionMenu.entry?.entry_group_size || 1) > 1) && (
            <MenuItem onClick={() => openEntryRequestDialog('update', 'group')}>Eintragungsgruppe bearbeiten</MenuItem>
          )}
          {canEditUserEntries && (entryActionMenu.pendingCreate
            ? Boolean(entryActionMenu.entry?.groupAnchorEntryId)
            : Number(entryActionMenu.entry?.entry_group_size || 1) > 1) && (
            <MenuItem onClick={() => openEntryRequestDialog('delete', 'group')}>Löschung der Eintragungsgruppe beantragen</MenuItem>
          )}
        </Menu>
        {entryRequestDialog?.action === 'update' && (
          <LogbookEntryEditDialog
            entryId={entryRequestDialog.entryId}
            open
            requestScope={entryRequestDialog.ownerMode ? null : entryRequestDialog.scope}
            onClose={() => setEntryRequestDialog(null)}
            onSaved={(count) => (entryRequestDialog.scope === 'pending-create'
              ? handlePendingRequestChanged('update')
              : entryRequestDialog.ownerMode
              ? handleOwnerEntryChanged('update')
              : handleEntryRequestSubmitted('update', count))}
          />
        )}
        {entryRequestDialog?.action === 'delete' && entryRequestDialog.ownerMode && (
          <LogbookEntryDeleteDialog
            entryId={entryRequestDialog.entryId}
            open
            onClose={() => setEntryRequestDialog(null)}
            onDeleted={() => handleOwnerEntryChanged('delete')}
          />
        )}
        {entryRequestDialog?.action === 'delete' && !entryRequestDialog.ownerMode && (
          <StatisticsLogbookDeleteRequestDialog
            entryId={entryRequestDialog.entryId}
            open
            scope={entryRequestDialog.scope}
            onClose={() => setEntryRequestDialog(null)}
            onSubmitted={(count) => handleEntryRequestSubmitted('delete', count)}
          />
        )}
        {entryRequestDialog?.action === 'cancel' && entryRequestDialog.scope === 'pending-create' && (
          <StatisticsPendingEntryCancelDialog
            requestId={entryRequestDialog.entryId}
            open
            onClose={() => setEntryRequestDialog(null)}
            onCancelled={() => handlePendingRequestChanged('cancel')}
          />
        )}
        <SelfReflectionQuickViewDialog
          open={Boolean(selfReflectionQuickView)}
          subject={selfReflectionQuickView}
          onClose={() => setSelfReflectionQuickView(null)}
        />
      </Stack>
    </Box>
  );
}

export default StatisticsView;
